import { inject, Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Output, EventEmitter, Input, ViewChild, ElementRef, HostBinding, HostListener } from '@angular/core';
import * as d3 from 'd3';
import { axisTickFormat } from '../chart-axis-format';
import { Subscription } from 'rxjs';
import { DataAggregationService, AggregationConfig } from 'src/app/services/data-aggregation.service';
import { FilterService } from 'src/app/services/filter.service'

import { NoDataNotifierService } from 'src/app/services/no-data-notifier.service';
@Component({
    selector: 'app-waterfall-chart',
    templateUrl: './waterfall-chart.component.html',
    styleUrls: ['./waterfall-chart.component.scss'],
    standalone: false
})
export class WaterfallChartComponent implements OnInit, OnDestroy, OnChanges {
  private readonly noDataNotifier = inject(NoDataNotifierService);

  @Input('data') inputData: any;
  @Input('item') item: any;
  @Input('pageKey') pageKey: any;
  @Input('config') config: any;
  @Input('headerConfig') headerConfig: any;
  @Input('error_name') error_name: any;
  @Input('heading') heading: string = '';
  @Output() barClicked = new EventEmitter<{ column: string; value: any }>();

  @ViewChild('fs', { static: true }) fs!: ElementRef;
  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;
  @HostBinding('class.is-fullscreen') isFullscreen = false;
  isActive = false;
  isLoading: boolean = true;
  noData: boolean = false;
  iconList: any = [];
  initiateChart: boolean = false;
  private resizeObserver: ResizeObserver | null = null;
  private resizeTimeout: any = null;
  private resizeSkipFirst = true;

  chartData: any = [];
  props: any = {};
  private static instanceCounter = 0;
  divId: any = 'waterfallChart_' + (++WaterfallChartComponent.instanceCounter);
  currentVisibleData: any = [];

  // Chart mode state
  activeChart: string = 'bar';
  activePeriod: string = 'M';

  // Legend toggle state: hidden series keys
  hiddenKeys: Set<string> = new Set();

  // D3 range slider state — primary (left Y) and secondary (right Y)
  private handleRadius = 6;
  lineMin: number = 0;
  lineMax: number = 0;
  lineMinRight: number = 0;
  lineMaxRight: number = 0;

  // Waterfall drill-down state
  waterfallActive: boolean = false;
  waterfallBarData: any = null;
  subs: Subscription[] = [];

  // Tooltip element
  private tooltipEl: HTMLDivElement | null = null;
  private boundMouseMove = (e: MouseEvent) => {
    if (this.tooltipEl?.classList.contains('show')) this.positionTooltip(e);
  };

  constructor(private dataAggregation: DataAggregationService,private filterService: FilterService) {
      this.subs.push(
          this.filterService.filterQuery.subscribe((query: any) => {
            this.isLoading = true;
          }))
     }

  @HostListener('fullscreenchange')
  @HostListener('webkitfullscreenchange')
  @HostListener('mozfullscreenchange')
  @HostListener('MSFullscreenChange')
  screenChange() {
    if (!document.fullscreenElement) {
      this.closeFullscreen();
    }
  }

  fullscreenChanges(fullscreen: any) {
    if (fullscreen == false) {
      this.closeFullscreen();
      return;
    }
    this.props.chartHeight = window.outerHeight - 60;
    this.isFullscreen = fullscreen;
    this.isActive = fullscreen;
    this.rebuildChart();
  }

  closeFullscreen(): void {
    this.props.chartHeight = 500;
    this.isFullscreen = false;
    this.isActive = false;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    setTimeout(() => {
      this.rebuildChart();
    }, 100);
  }

  private setupResizeObserver(): void {
    const container = this.chartContainer?.nativeElement;
    if (container) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.resizeSkipFirst) {
          this.resizeSkipFirst = false;
          return;
        }
        if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          if (this.chartData.length > 0 && this.props.chartHeight) {
            this.rebuildChart();
          }
        }, 200);
      });
      this.resizeObserver.observe(container);
    }
  }

  // ── Tooltip hooks ──
  private showTooltip(myType: any, myData: any, myX: any, myY: any, chartWidth: any): void {
    if (!this.tooltipEl) return;

    const suffix = this.props.yAxisSuffix || '';

    const fmtVal = (val: any): string => {
      if (val === null || val === undefined) return '';
      if (typeof val !== 'number') return val;
      return (val < 1 && val > 0 ? val.toFixed(1) : (Number.isInteger(val) ? d3.format(',')(val) : d3.format(',.1f')(val))) + suffix;
    };

    const buildRow = (key: string, val: any): string => {
      if (val === null || val === undefined) return '';
      const colour = key === '_total' ? '#1363DF' : (this.props.colours[key] || '#fff');
      const label = key === '_total' ? 'TOTAL' : (this.props.legendLabels[key] || key).toUpperCase();
      return '<div style="display:flex;align-items:center;gap:6px;padding:3px 0">' +
        '<span style="width:8px;height:8px;border-radius:50%;background:' + colour + ';flex-shrink:0"></span>' +
        '<span style="flex:1;font-weight:500;font-size:9px;letter-spacing:0.2px;font-family:Poppins,sans-serif">' + label + '</span>' +
        '<span style="font-weight:600;margin-left:8px;font-size:9px;white-space:nowrap;font-family:Poppins,sans-serif">' + fmtVal(val) + '</span></div>';
    };

    // Format date as title
    const date = myData.date ? new Date(myData.date) : null;
    let titleDate = '';
    if (date) {
      const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
      titleDate = monthNames[date.getMonth()] + ' ' + date.getFullYear();
    }
    const chartTitle = ((this.item?.config?.heading || this.heading || 'CHART').toUpperCase()) + (titleDate ? ', ' + titleDate : '');

    const barKeys = (this.props.barKeys || []).filter((k: string) => !this.hiddenKeys.has(k));
    const lineKeys = (this.props.lineKeys || []).filter((k: string) => !this.hiddenKeys.has(k));
    const areaKeys = (this.props.areaKeys || []).filter((k: string) => !this.hiddenKeys.has(k));
    const areaOnlyKeys = areaKeys.filter((k: string) => lineKeys.indexOf(k) === -1);

    let html = '';
    html += '<div style="padding:5px 10px;border-bottom:1px solid #333;font-weight:600;font-size:10px;letter-spacing:0.3px;color:#fff;font-family:Poppins,sans-serif">' + chartTitle + '</div>';

    if (barKeys.length > 0) {
      html += '<div style="padding:3px 10px;background:#2A2A2A">';
      barKeys.forEach((key: string) => { html += buildRow(key, myData[key]); });
      html += '</div>';
    }

    if (lineKeys.length > 0) {
      html += '<div style="padding:3px 10px;' + (barKeys.length > 0 ? 'border-top:1px solid #444;' : '') + 'background:#333">';
      lineKeys.forEach((key: string) => { html += buildRow(key, myData[key]); });
      html += '</div>';
    }

    if (areaOnlyKeys.length > 0) {
      html += '<div style="padding:3px 10px;' + (barKeys.length > 0 || lineKeys.length > 0 ? 'border-top:1px solid #444;' : '') + 'background:#333">';
      areaOnlyKeys.forEach((key: string) => { html += buildRow(key, myData[key]); });
      html += '</div>';
    }

    this.tooltipEl.innerHTML = html;
    this.tooltipEl.classList.add('show');

    const containerRect = this.fs.nativeElement.getBoundingClientRect();
    const ttW = this.tooltipEl.offsetWidth || 200;
    const ttH = this.tooltipEl.offsetHeight || 100;
    const containerW = containerRect.width;
    const containerH = containerRect.height;
    const gap = 12;

    const relX = myX - containerRect.left;
    const relY = myY - containerRect.top;

    let left = relX + gap;
    if (left + ttW > containerW) left = relX - ttW - gap;
    left = Math.max(0, Math.min(left, containerW - ttW));

    let top = relY - ttH - gap;
    if (top < 0) top = relY + gap;
    top = Math.max(0, Math.min(top, containerH - ttH));

    this.tooltipEl.style.left = left + 'px';
    this.tooltipEl.style.top = top + 'px';
  }

  private hideTooltip(myType: any): void {
    this.tooltipEl?.classList.remove('show');
  }

  private positionTooltip(e: MouseEvent): void {
    if (!this.tooltipEl) return;
    const containerRect = this.fs.nativeElement.getBoundingClientRect();
    const relX = e.clientX - containerRect.left;
    const relY = e.clientY - containerRect.top;
    const ttW = this.tooltipEl.offsetWidth || 200;
    const ttH = this.tooltipEl.offsetHeight || 100;
    const containerW = containerRect.width;
    const containerH = containerRect.height;
    const gap = 12;

    let left = relX + gap;
    if (left + ttW > containerW) left = relX - ttW - gap;
    left = Math.max(0, Math.min(left, containerW - ttW));

    let top = relY - ttH - gap;
    if (top < 0) top = relY + gap;
    top = Math.max(0, Math.min(top, containerH - ttH));

    this.tooltipEl.style.left = left + 'px';
    this.tooltipEl.style.top = top + 'px';
  }

  // ── Lifecycle ──
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['inputData'] && changes['inputData'].currentValue != changes['inputData'].previousValue && this.initiateChart) {
      this.getChartData();
    }
  }

  ngOnInit(): void {
    this.iconList = this.item?.config?.icon ? this.item.config.icon : this.iconList;
    this.divId = this.item?.config?.divId ? this.item.config.divId + this.item.widget_uniq_key : this.divId;

    this.tooltipEl = document.createElement('div');
    this.tooltipEl.className = 'sc-tt';
    this.fs.nativeElement.appendChild(this.tooltipEl);
    document.addEventListener('mousemove', this.boundMouseMove);

    this.initiateCharts();
    this.initiateChart = true;
    this.setupResizeObserver();
  }

  ngOnDestroy(): void {
    document.removeEventListener('mousemove', this.boundMouseMove);
    this.tooltipEl?.remove();
    this.tooltipEl = null;
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  // ── Legend toggle ──
  isHidden(key: string): boolean {
    return this.hiddenKeys.has(key);
  }

  toggleKey(key: string): void {
    const s = new Set(this.hiddenKeys);
    const allKeys = [...(this.props.barKeys || []), ...(this.props.lineKeys || []), ...(this.props.areaKeys || [])];
    const uniqueKeys = [...new Set(allKeys)];
    if (s.has(key)) {
      s.delete(key);
    } else {
      if (s.size >= uniqueKeys.length - 1) return;
      s.add(key);
    }
    this.hiddenKeys = s;
    // Reset slider zoom on legend toggle
    this.lineMin = 0;
    this.lineMax = 0;
    this.lineMinRight = 0;
    this.lineMaxRight = 0;
    this.rebuildChart();
  }

  rebuildChart(): void {
    if (!this.chartContainer) return;
    const svg = d3.select(this.chartContainer.nativeElement).select('svg');
    if (!svg.empty()) {
      svg.selectAll('*').interrupt();
      svg.interrupt();
      svg.remove();
    }
    this.initiateCharts();
    if (this.chartData && this.chartData.length > 0) {
      this.noData = false;
      this.isLoading = false;
      if (this.waterfallActive && this.waterfallBarData) {
        this.plotChart();
        this.plotWaterfallDrillDown();
      } else {
        this.plotChart();
      }
    }
  }

  // ══════════════════════════════════════════════════════
  // STEP 1: initiateCharts() — One-Time SVG Scaffolding
  // ══════════════════════════════════════════════════════
  initiateCharts(): void {
    const myClass = this.divId;

    const existing = d3.select(this.chartContainer.nativeElement).select('svg');
    if (!existing.empty()) {
      existing.selectAll('*').interrupt();
      existing.remove();
    }

    const mySvg = d3.select(this.chartContainer.nativeElement)
      .append('svg')
      .attr('id', 'svg_' + myClass)
      .attr('width', '100%')
      .style('background-color', 'white');

    mySvg.append('text').attr('id', 'noDataMessage' + myClass);

    const defs = mySvg.append('defs');

    defs.append('clipPath').attr('id', 'brushClip' + myClass)
      .append('rect').attr('id', 'brushClipRect' + myClass);

    // Drop shadow filter
    const filter = defs.append('filter').attr('id', 'drop-shadow-' + myClass).attr('width', 10).attr('height', 24);
    filter.append('feGaussianBlur').attr('in', 'SourceAlpha').attr('stdDeviation', 1).attr('result', 'blur');
    filter.append('feOffset').attr('in', 'blur').attr('dx', 1).attr('dy', 1).attr('result', 'offsetBlur');
    filter.append('feFlood').attr('in', 'offsetBlur').attr('flood-color', '#000000').attr('flood-opacity', 0.4).attr('result', 'offsetColor');
    filter.append('feComposite').attr('in', 'offsetColor').attr('in2', 'offsetBlur').attr('operator', 'in').attr('result', 'offsetBlur');
    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'offsetBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Area gradient
    const areaGradient = defs.append('linearGradient')
      .attr('id', 'areaGradient' + myClass)
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '0%').attr('y2', '100%');
    areaGradient.append('stop').attr('offset', '0%').attr('stop-color', '#FFDCC8').attr('stop-opacity', 0.8);
    areaGradient.append('stop').attr('offset', '100%').attr('stop-color', '#FFDCC8').attr('stop-opacity', 0.15);

    // Axes
    mySvg.append('g').attr('id', 'xAxis' + myClass).attr('class', 'axis' + myClass);
    mySvg.append('g').attr('id', 'yAxis' + myClass).attr('class', 'axis' + myClass);
    mySvg.append('g').attr('id', 'yAxisRight' + myClass).attr('class', 'axis' + myClass);

    // Partial period line
    mySvg.append('line').attr('class', 'partialPeriodLine' + myClass);
    mySvg.append('text').attr('class', 'partialPeriodLineLabel' + myClass);

    // Axis labels
    mySvg.append('text').attr('id', 'xAxisLabel' + myClass);
    mySvg.append('text').attr('id', 'yAxisLabel' + myClass);
    mySvg.append('text').attr('id', 'yAxisRightLabel' + myClass);

    // Legend group
    mySvg.append('g').attr('id', 'legendGroup' + myClass);

    // Chart group
    mySvg.append('g').attr('id', 'chartGroup' + myClass);

    // Brush group
    mySvg.append('g').attr('id', 'brushGroup' + myClass);
    mySvg.append('g').attr('id', 'brushSelectionGroup' + myClass);

    // Handle lines
    mySvg.append('line').attr('class', 'handleLines' + myClass + ' handleLeftLine1' + myClass);
    mySvg.append('line').attr('class', 'handleLines' + myClass + ' handleLeftLine2' + myClass);
    mySvg.append('line').attr('class', 'handleLines' + myClass + ' handleRightLine1' + myClass);
    mySvg.append('line').attr('class', 'handleLines' + myClass + ' handleRightLine2' + myClass);

    // Hover guide line
    mySvg.append('line').attr('id', 'hoverLine' + myClass);
    // hover crosshair lines (horizontal) — one per key, plus value labels
    mySvg.append('g').attr('id', 'hoverCrosshairGroup' + myClass)
      .attr('pointer-events', 'none');

    // Slider group
    mySvg.append('g').attr('id', 'sliderGroup' + myClass).attr('class', 'slider' + myClass);

    this.initiateChart = true;
  }

  // ══════════════════════════════════════════════════════
  // STEP 2: plotChart() — Config-Driven Multi-Mode Chart
  // ══════════════════════════════════════════════════════
  plotChart(): void {
    const myChart = this;
    const myClass = myChart.divId;
    const mySvg: any = d3.select('#svg_' + myClass);
    const width: any = mySvg.node().getBoundingClientRect().width;
    const height = myChart.props.chartHeight;

    const lineDot = myChart.props.lineDot || {};
    const circleRadius = lineDot.radius != null ? lineDot.radius : 4;
    const dotFill = lineDot.fill || 'white';
    const dotStrokeWidth = lineDot.strokeWidth != null ? lineDot.strokeWidth : 2;
    const dotShow = lineDot.show !== false;

    const dataLabels = myChart.props.dataLabels || {};
    const dlShowBar = dataLabels.bar === true;
    const dlShowLine = dataLabels.line === true;
    const dlShowArea = dataLabels.area === true;
    const dlShowTotal = dataLabels.total === true;
    const dlShowValue = dataLabels.value !== false;
    const dlFontSize = dataLabels.fontSize || 11;
    const dlFontWeight = dataLabels.fontWeight || 600;
    const dlTotalFontWeight = dataLabels.totalFontWeight || 700;
    const dlColor = dataLabels.color || '#333';
    const dlTotalColor = dataLabels.totalColor || dlColor;
    const dlFormat = dataLabels.format || '-2~s';

    mySvg.attr('height', height);

    let margins: any = { left: 75, right: 65, top: 60, bottom: 10, brush: 52, mid: 80 };
    if (myChart.props.showBrush === false) {
      margins.brush = 0;
      margins.mid = 50;
      margins.bottom = 0;
    }
    const chartHeight = height - margins.top - margins.bottom - margins.brush - margins.mid;
    const plotWidth = width - margins.left - margins.right;

    // Data period
    const dataPeriod = 'M';
    const timeConverter: any = { "D": d3.timeDay, "W": d3.timeWeek, "M": d3.timeMonth, "Q": d3.timeMonth, "Y": d3.timeYear };
    const displayPeriod = myChart.activePeriod || 'M';

    const xTickFormat: any = (d: any) => {
      const date = new Date(d);
      if (displayPeriod === 'D') return d3.timeFormat('%d %b %Y')(date);
      if (displayPeriod === 'W') return d3.timeFormat('%d %b %Y')(date);
      if (displayPeriod === 'Q') {
        const q = Math.ceil((date.getMonth() + 1) / 3);
        return 'Q' + q + ' ' + d3.timeFormat('%Y')(date);
      }
      if (displayPeriod === 'Y') return d3.timeFormat('%Y')(date);
      return d3.timeFormat('%b\n%Y')(date);
    };

    // --- DATA TRANSFORMATION ---
    let chartData: any = JSON.parse(JSON.stringify(myChart.chartData));
    chartData.map((m: any) => m.date = new Date(m[myChart.props.xAxisVar]));
    let dateGroup = Array.from(d3.group(chartData, (g: any) => g.date));
    dateGroup = dateGroup.sort((a: any, b: any) => d3.ascending(a[0], b[0]));
    const dateGroupDates: any = [];
    chartData = [];

    // All keys (used for legend display)
    const allBarKeys = myChart.props.barKeys || [];
    const allLineKeys = myChart.props.lineKeys || [];
    const allAreaKeys = myChart.props.areaKeys || [];

    // Visible keys (filtered by legend toggle)
    const barKeys = allBarKeys.filter((k: string) => !myChart.hiddenKeys.has(k));
    const lineKeys = allLineKeys.filter((k: string) => !myChart.hiddenKeys.has(k));
    const areaKeys = allAreaKeys.filter((k: string) => !myChart.hiddenKeys.has(k));
    const barMode = myChart.props.barMode || 'clustered';

    const isStackedArea = myChart.props.stackedArea === true;

    // Aggregate data by date
    dateGroup.forEach((d: any) => {
      dateGroupDates.push(d[0]);
      const row: any = { date: d[0] };
      allBarKeys.forEach((key: string) => {
        row[key] = d3.sum(d[1], (s: any) => s[key]);
      });
      allLineKeys.forEach((key: string) => {
        row[key] = d3.mean(d[1], (s: any) => s[key]);
      });
      allAreaKeys.forEach((key: string) => {
        if (allLineKeys.indexOf(key) === -1) {
          row[key] = d3.mean(d[1], (s: any) => s[key]);
        }
      });
      // Stacked bar positions
      if (barMode === 'stacked') {
        let cumulative = 0;
        barKeys.forEach((key: string) => {
          row[key + '_y0'] = cumulative;
          cumulative += row[key];
          row[key + '_y1'] = cumulative;
        });
        row._stackTotal = cumulative;
        let cumulativeAll = 0;
        allBarKeys.forEach((key: string) => { cumulativeAll += row[key] || 0; });
        row._stackTotalAll = cumulativeAll;
      }
      // Stacked area positions
      if (isStackedArea) {
        let cumulative = 0;
        areaKeys.forEach((key: string) => {
          row[key + '_y0'] = cumulative;
          cumulative += row[key];
          row[key + '_y1'] = cumulative;
        });
        row._areaStackTotal = cumulative;
      }
      chartData.push(row);
    });

    // --- SCALES ---
    const tickValues = myChart.props.d3AxisFormatting === true ? null : dateGroupDates;
    const tickCount = myChart.props.d3AxisFormatting === true ? 6 : null;

    const lineOnPrimary = myChart.props.lineOnPrimaryAxis === true;

    // yMax for bar axis (left)
    let yMaxBar: any;
    if (isStackedArea) {
      yMaxBar = d3.max(chartData, (m: any) => m._areaStackTotal) || 0;
    } else if (barMode === 'stacked') {
      yMaxBar = d3.max(chartData, (m: any) => m._stackTotalAll || m._stackTotal);
    } else if (allBarKeys.length > 0) {
      yMaxBar = d3.max(chartData, (m: any) => {
        return d3.max(allBarKeys, (key: string) => m[key]);
      });
    } else {
      yMaxBar = 0;
    }

    if (lineOnPrimary) {
      const allPrimaryKeys = [...new Set([...allLineKeys, ...allAreaKeys])];
      const lineAreaMax: any = allPrimaryKeys.length > 0
        ? d3.max(chartData, (m: any) => d3.max(allPrimaryKeys, (key: string) => m[key]))
        : 0;
      yMaxBar = Math.max(yMaxBar || 0, lineAreaMax || 0);
    }
    if (!yMaxBar || yMaxBar <= 0) yMaxBar = 10;

    // yMax for right axis
    let yMaxLine: any = 0;
    const allSecondaryKeys = (isStackedArea || lineOnPrimary) ? [] : [...new Set([...allLineKeys, ...allAreaKeys])];
    if (allSecondaryKeys.length > 0) {
      yMaxLine = d3.max(chartData, (m: any) => {
        return d3.max(allSecondaryKeys, (key: string) => m[key]);
      });
    }

    const hasDualAxis = allSecondaryKeys.length > 0 && allBarKeys.length > 0;

    let xExtent: any = d3.extent(chartData, (d: any) => d.date);
    const xMax = d3.max(chartData, (d: any) => d.date);
    xExtent[0] = timeConverter[dataPeriod].offset(xExtent[0], -1);
    xExtent[1] = timeConverter[dataPeriod].offset(xExtent[1], 1);

    const yMaxBarFull = Number(yMaxBar) * 1.1;
    const yMaxLineFull = Number(yMaxLine) * 1.2;

    // Sync slider ranges
    if (yMaxBarFull > 0) {
      if (myChart.lineMax <= 0 || myChart.lineMax === 100 || myChart.lineMax > yMaxBarFull * 1.5 || Math.abs(myChart.lineMax - yMaxBarFull) / yMaxBarFull > 0.5) {
        myChart.lineMin = 0;
        myChart.lineMax = yMaxBarFull;
      }
    }
    if (yMaxLineFull > 0) {
      if (myChart.lineMaxRight <= 0 || myChart.lineMaxRight === 100 || myChart.lineMaxRight > yMaxLineFull * 1.5 || Math.abs(myChart.lineMaxRight - yMaxLineFull) / yMaxLineFull > 0.5) {
        myChart.lineMinRight = 0;
        myChart.lineMaxRight = yMaxLineFull;
      }
    } else {
      myChart.lineMinRight = 0;
      myChart.lineMaxRight = 0;
    }

    // Visible rows for brush
    let startingVisibleRows: any = parseInt(String(plotWidth / 25));
    startingVisibleRows = startingVisibleRows > chartData.length ? chartData.length : startingVisibleRows;
    const brushStartX = chartData.length === 0 ? 0 : (1 - (startingVisibleRows / chartData.length)) * plotWidth;

    // Scales
    const yScale = d3.scaleLinear().domain([myChart.lineMin, myChart.lineMax]).range([chartHeight, 0]);
    const yScaleBrush = d3.scaleLinear().domain([0, yMaxBarFull]).range([margins.brush, 0]);
    const yScaleRight = d3.scaleLinear().domain([myChart.lineMinRight, myChart.lineMaxRight]).range([chartHeight, 0]);
    const yScaleRightBrush = d3.scaleLinear().domain([0, yMaxLineFull]).range([margins.brush, 0]);
    const xScaleAll: any = d3.scaleTime().range([0, plotWidth]).domain(xExtent);
    const xScale: any = d3.scaleTime().range([0, plotWidth]).domain(xExtent);

    // Brush
    const brush: any = d3.brushX()
      .handleSize(10)
      .extent([[0, 0], [plotWidth, margins.brush]])
      //@ts-ignore
      .on('start brush end', !myChart.props.showBrush ? null : brushed);

    // --- NO DATA MESSAGE ---
    mySvg.select('#noDataMessage' + myClass)
      .attr('x', margins.left + plotWidth / 2)
      .attr('y', margins.top + chartHeight / 2)
      .attr('font-family', 'Poppins')
      .attr('fill', '#101D42')
      .attr('font-size', '16px')
      .attr('font-weight', '500')
      .attr('text-anchor', 'middle')
      .text(chartData.length === 0 ? 'There is no data for this time period' : '');

    // --- INTERACTIVE LEGEND ---
    const legendGroup = mySvg.select('#legendGroup' + myClass);
    legendGroup.selectAll('*').remove();
    const legendMid = (margins.top / 2) - 20;
    const legendVis = chartData.length === 0 ? 'hidden' : 'visible';

    function measureWidth(myText: any, myFontSize: any): any {
      const context: any = document.createElement('canvas').getContext('2d');
      context.font = myFontSize + 'px Poppins, sans-serif';
      return context.measureText(myText).width;
    }

    // Build all legend items: bars first, then areas, then lines
    const allLegendItems: any[] = [];
    allBarKeys.forEach((key: string) => allLegendItems.push({ key, colour: myChart.props.colours[key], type: 'rect' }));
    allAreaKeys.forEach((key: string) => allLegendItems.push({ key, colour: myChart.props.colours[key], type: 'area' }));
    allLineKeys.forEach((key: string) => {
      if (allAreaKeys.indexOf(key) === -1) allLegendItems.push({ key, colour: myChart.props.colours[key], type: 'line' });
    });

    // Measure each legend item
    const legendItemSpacing = 22;
    const iconGap = 6; // gap between icon and label text
    const allLegendMeta: any[] = allLegendItems.map((item: any) => {
      const label = (myChart.props.legendLabels[item.key] || item.key).toUpperCase();
      const iconW = item.type === 'rect' ? 10 : item.type === 'area' ? 16 : 16;
      const textW = measureWidth(label, 12);
      return { ...item, label, iconW, textW, totalW: iconW + iconGap + textW };
    });
    const legendTotalWidth = allLegendMeta.reduce((sum: number, d: any) => sum + d.totalW + legendItemSpacing, 0);
    const arrowSpace = 30;
    const legendFits = legendTotalWidth <= width;
    const visibleAreaLeft = legendFits ? 0 : arrowSpace;
    const visibleAreaWidth = legendFits ? width : width - arrowSpace * 2;

    // Clip-path for legend scrolling area
    const legendClipId = 'legendClip' + myClass;
    let legendClip = mySvg.select('defs').select('#' + legendClipId);
    if (legendClip.empty()) {
      legendClip = mySvg.select('defs').append('clipPath').attr('id', legendClipId);
      legendClip.append('rect');
    }
    legendClip.select('rect')
      .attr('x', visibleAreaLeft).attr('y', legendMid - 15)
      .attr('width', visibleAreaWidth).attr('height', 30);

    // Scrollable inner group — holds all legend items, translated on scroll
    const legendInner = legendGroup.append('g')
      .attr('class', 'legendInner')
      .attr('clip-path', legendFits ? null : 'url(#' + legendClipId + ')');

    const legendContent = legendInner.append('g').attr('class', 'legendContent');

    // Render ALL legend items inside legendContent
    let lx = legendFits ? (width - legendTotalWidth) / 2 : arrowSpace;
    allLegendMeta.forEach((item: any) => {
      const isHidden = myChart.hiddenKeys.has(item.key);
      const itemG = legendContent.append('g')
        .attr('class', 'legendItemGroup')
        .style('cursor', 'pointer')
        .style('opacity', isHidden ? 0.3 : 1)
        .on('click', () => { myChart.toggleKey(item.key); });

      if (item.type === 'rect') {
        itemG.append('rect')
          .attr('x', lx).attr('y', legendMid - 5)
          .attr('width', 6).attr('height', 10)
          .attr('rx', 1.5).attr('fill', item.colour)
          .attr('visibility', legendVis);
        lx += 6 + iconGap;
      } else if (item.type === 'area') {
        const iW = 12, iH = 8;
        itemG.append('path')
          .attr('d', 'M' + lx + ',' + (legendMid + iH / 2) +
            ' Q' + (lx + iW * 0.25) + ',' + (legendMid - iH / 2 - 1.5) +
            ' ' + (lx + iW * 0.5) + ',' + (legendMid - iH / 2) +
            ' Q' + (lx + iW * 0.75) + ',' + (legendMid - iH / 2 - 2) +
            ' ' + (lx + iW) + ',' + (legendMid + iH / 2) + ' Z')
          .attr('fill', item.colour).attr('opacity', 0.4)
          .attr('stroke', item.colour).attr('stroke-width', 1.2)
          .attr('visibility', legendVis);
        lx += iW + iconGap;
      } else {
        itemG.append('line')
          .attr('x1', lx).attr('y1', legendMid)
          .attr('x2', lx + 10).attr('y2', legendMid)
          .attr('stroke', item.colour).attr('stroke-width', 2)
          .attr('visibility', legendVis);
        itemG.append('circle')
          .attr('cx', lx + 5).attr('cy', legendMid)
          .attr('r', 2.5).attr('fill', item.colour)
          .attr('visibility', legendVis);
        lx += 10 + iconGap;
      }
      itemG.append('text')
        .attr('x', lx).attr('y', legendMid + 5)
        .attr('font-family', 'Poppins')
        .attr('fill', isHidden ? '#B0B0B0' : '#101D42')
        .attr('font-size', '12px').attr('font-weight', '500')
        .attr('visibility', legendVis)
        .text(item.label?.toUpperCase());
      lx += item.textW + legendItemSpacing;
    });

    // Smooth scroll arrows (only when legend overflows)
    if (!legendFits) {
      let scrollOffset = 0;
      const scrollStep = 80;
      const maxScroll = Math.max(0, legendTotalWidth - visibleAreaWidth);

      function updateArrowVisibility(): void {
        arrowLeftG.attr('visibility', scrollOffset > 0 ? 'visible' : 'hidden');
        arrowRightG.attr('visibility', scrollOffset < maxScroll ? 'visible' : 'hidden');
      }

      // Left arrow group
      const arrowLeftG = legendGroup.append('g')
        .attr('class', 'legendArrowLeft')
        .style('cursor', 'pointer')
        .attr('visibility', 'hidden')
        .on('click', () => {
          scrollOffset = Math.max(0, scrollOffset - scrollStep);
          legendContent.transition().duration(300).ease(d3.easeCubicOut)
            .attr('transform', 'translate(' + (-scrollOffset) + ',0)');
          updateArrowVisibility();
        });
      const arrowR = 8;
      const arrowLeftCx = 10;
      const arrowRightCx = width - 10;
      arrowLeftG.append('circle')
        .attr('r', arrowR).attr('fill', '#F5F7FA').attr('stroke', '#E0E4EA')
        .attr('stroke-width', 1).attr('cx', arrowLeftCx).attr('cy', legendMid - 1);
      arrowLeftG.append('path')
        .attr('d', 'M' + (arrowLeftCx + 2) + ',' + (legendMid - 4) + ' L' + (arrowLeftCx - 2.5) + ',' + (legendMid - 1) + ' L' + (arrowLeftCx + 2) + ',' + (legendMid + 2))
        .attr('fill', 'none').attr('stroke', '#1363DF').attr('stroke-width', 1.5)
        .attr('stroke-linecap', 'round').attr('stroke-linejoin', 'round');

      // Right arrow group
      const arrowRightG = legendGroup.append('g')
        .attr('class', 'legendArrowRight')
        .style('cursor', 'pointer')
        .attr('visibility', maxScroll > 0 ? 'visible' : 'hidden')
        .on('click', () => {
          scrollOffset = Math.min(maxScroll, scrollOffset + scrollStep);
          legendContent.transition().duration(300).ease(d3.easeCubicOut)
            .attr('transform', 'translate(' + (-scrollOffset) + ',0)');
          updateArrowVisibility();
        });
      arrowRightG.append('circle')
        .attr('r', arrowR).attr('fill', '#F5F7FA').attr('stroke', '#E0E4EA')
        .attr('stroke-width', 1).attr('cx', arrowRightCx).attr('cy', legendMid - 1);
      arrowRightG.append('path')
        .attr('d', 'M' + (arrowRightCx - 2) + ',' + (legendMid - 4) + ' L' + (arrowRightCx + 2.5) + ',' + (legendMid - 1) + ' L' + (arrowRightCx - 2) + ',' + (legendMid + 2))
        .attr('fill', 'none').attr('stroke', '#1363DF').attr('stroke-width', 1.5)
        .attr('stroke-linecap', 'round').attr('stroke-linejoin', 'round');
    }

    // --- CLIP RECT ---
    mySvg.select('.overlay').style('fill', 'transparent');
    mySvg.select('#brushClipRect' + myClass)
      .attr('x', 0).attr('y', 0)
      .attr('width', plotWidth).attr('height', chartHeight)
      .attr('transform', 'translate(' + margins.left + ',' + margins.top + ')');

    // --- AXIS LABELS ---
    mySvg.select('#yAxisLabel' + myClass)
      .attr('visibility', chartData.length === 0 ? 'hidden' : 'visible')
      .attr('x', margins.left)
      .attr('y', margins.top - 14)
      .attr('text-anchor', 'start')
      .attr('font-family', 'Poppins')
      .attr('fill', myChart.props.yAxisLabelColour || '#737D88')
      .attr('font-size', '12px')
      .attr('font-weight', '400')
      .attr('transform', '')
      .text(myChart.props.yAxisLabel?.toUpperCase() || '');

    mySvg.select('#yAxisRightLabel' + myClass)
      .attr('visibility', chartData.length === 0 || !hasDualAxis ? 'hidden' : 'visible')
      .attr('x', margins.left + plotWidth)
      .attr('y', margins.top - 14)
      .attr('text-anchor', 'end')
      .attr('font-family', 'Poppins')
      .attr('fill', myChart.props.yAxisRightLabelColour || '#737D88')
      .attr('font-size', '12px')
      .attr('font-weight', '400')
      .attr('transform', '')
      .text(myChart.props.yAxisRightLabel?.toUpperCase() || '' || '');

    mySvg.select('#xAxisLabel' + myClass)
      .attr('visibility', chartData.length === 0 ? 'hidden' : 'visible')
      .attr('x', margins.left + plotWidth / 2)
      .attr('y', height - margins.bottom - margins.brush - 10)
      .attr('text-anchor', 'middle')
      .attr('font-family', 'Poppins')
      .attr('fill', '#737D88')
      .attr('font-size', '12px')
      .text(myChart.props.xAxisLabel);

    // --- AXES ---
    mySvg.select('#xAxis' + myClass)
      .call(d3.axisBottom(xScale).tickSizeOuter(0).tickValues(tickValues).ticks(tickCount).tickFormat(xTickFormat))
      .attr('transform', 'translate(' + margins.left + ',' + (margins.top + chartHeight) + ')');

    mySvg.select('#yAxis' + myClass)
      //@ts-ignore
      .call(d3.axisLeft(yScale).tickSizeOuter(0)
        .ticks(7)
        .tickFormat((d: any) => {
          return axisTickFormat(myChart.props.yAxisFormatting)(d) + (myChart.props.yAxisSuffix || '');
        }))
      .attr('transform', 'translate(' + margins.left + ',' + margins.top + ')');

    if (hasDualAxis) {
      mySvg.select('#yAxisRight' + myClass)
        .attr('visibility', 'visible')
        .call(d3.axisRight(yScaleRight).tickSizeOuter(0).ticks(7).tickFormat((d: any) => axisTickFormat(myChart.props.yAxisRightFormatting || '-2~s')(d) + (myChart.props.yAxisRightSuffix || '')))
        .attr('transform', 'translate(' + (margins.left + plotWidth) + ',' + margins.top + ')');
    } else {
      mySvg.select('#yAxisRight' + myClass).attr('visibility', 'hidden');
    }

    mySvg.selectAll('.axis' + myClass + ' path')
      .attr('visibility', chartData.length === 0 ? 'hidden' : 'visible')
      .style('stroke', '#E8EAEE');

    // --- LINE/AREA GENERATORS ---
    const areaGenerators: any = {};
    const areaGeneratorsBrush: any = {};
    areaKeys.forEach((key: string) => {
      if (isStackedArea) {
        areaGenerators[key] = d3.area()
          .curve(d3.curveCardinal)
          .x((d: any) => xScale(d.date))
          .y0((d: any) => yScale(d[key + '_y0']))
          .y1((d: any) => yScale(d[key + '_y1']));
        areaGeneratorsBrush[key] = d3.area()
          .curve(d3.curveCardinal)
          .x((d: any) => xScaleAll(d.date))
          .y0((d: any) => yScaleBrush(d[key + '_y0']))
          .y1((d: any) => yScaleBrush(d[key + '_y1']));
      } else {
        const yS = lineOnPrimary ? yScale : yScaleRight;
        const ySB = lineOnPrimary ? yScaleBrush : yScaleRightBrush;
        areaGenerators[key] = d3.area()
          .curve(d3.curveCardinal)
          .x((d: any) => xScale(d.date))
          .y0(chartHeight)
          .y1((d: any) => yS(d[key]));
        areaGeneratorsBrush[key] = d3.area()
          .curve(d3.curveCardinal)
          .x((d: any) => xScaleAll(d.date))
          .y0(margins.brush)
          .y1((d: any) => ySB(d[key]));
      }
    });

    // Line generators
    const lineGenerators: any = {};
    const lineGeneratorsBrush: any = {};
    if (isStackedArea) {
      areaKeys.forEach((key: string) => {
        lineGenerators[key] = d3.line()
          .curve(d3.curveCardinal)
          .x((d: any) => xScale(d.date))
          .y((d: any) => yScale(d[key + '_y1']));
        lineGeneratorsBrush[key] = d3.line()
          .curve(d3.curveCardinal)
          .x((d: any) => xScaleAll(d.date))
          .y((d: any) => yScaleBrush(d[key + '_y1']));
      });
    }
    const lineScaleKeys = lineOnPrimary ? [...new Set([...lineKeys, ...areaKeys])] : allSecondaryKeys;
    const yLineScale = lineOnPrimary ? yScale : yScaleRight;
    const yLineScaleBrush = lineOnPrimary ? yScaleBrush : yScaleRightBrush;
    lineScaleKeys.forEach((key: string) => {
      if (!lineGenerators[key]) {
        lineGenerators[key] = d3.line()
          .curve(d3.curveCardinal)
          .defined((d: any) => d[key] !== null && d[key] !== undefined)
          .x((d: any) => xScale(d.date))
          .y((d: any) => yLineScale(d[key]));
        lineGeneratorsBrush[key] = d3.line()
          .curve(d3.curveCardinal)
          .defined((d: any) => d[key] !== null && d[key] !== undefined)
          .x((d: any) => xScaleAll(d.date))
          .y((d: any) => yLineScaleBrush(d[key]));
      }
    });

    // --- CHART GROUP DATA ---
    const chartGroupData = myChart.props.showBrush === false ? ['chart'] : ['brush', 'chart'];
    let isInitialRender = false;
    let brushReady = false;

    // --- BRUSH SETUP ---
    mySvg.select('#brushGroup' + myClass)
      .attr('transform', 'translate(' + margins.left + ',' + (margins.top + chartHeight + margins.mid) + ')')
      .call(brush)
      .call(brush.move, [brushStartX, plotWidth]);

    if (myChart.props.showBrush === false) {
      mySvg.select('#brushGroup' + myClass)
        .call(brush.move, null)
        .selectAll('*').remove();
    }

    mySvg.selectAll('.handleLines' + myClass)
      .attr('visibility', chartData.length === 0 || myChart.props.showBrush === false ? 'hidden' : 'visible')
      .attr('y1', margins.top + chartHeight + margins.mid + (margins.brush - 12) / 2)
      .attr('y2', margins.top + chartHeight + margins.mid + 12 + (margins.brush - 12) / 2)
      .style('stroke', '#8A98AB')
      .style('stroke-width', '1')
      .attr('transform', 'translate(' + margins.left + ',0)');

    mySvg.selectAll('#brushGroup' + myClass)
      .selectAll('.selection')
      .attr('fill', '#A0A0A0')
      .attr('fill-opacity', '0.2')
      .style('stroke', '#E8EAEE')
      .style('stroke-width', '1');

    brushReady = true;

    // Single drawChart call with animation
    isInitialRender = true;
    drawChart();
    isInitialRender = false;

    // =========================================================
    // D3 Range Sliders
    // =========================================================
    const slider = mySvg.select('#sliderGroup' + myClass);
    slider.selectAll('*').remove();

    if (myChart.props.slider) {
      createRangeSlider('primary', 0, yMaxBarFull, 'left', yScale, '#yAxis' + myClass,
        () => myChart.lineMin, (v: number) => myChart.lineMin = v,
        () => myChart.lineMax, (v: number) => myChart.lineMax = v,
        '#e8eaee');
      if (hasDualAxis) {
        createRangeSlider('secondary', 0, yMaxLineFull, 'right', yScaleRight, '#yAxisRight' + myClass,
          () => myChart.lineMinRight, (v: number) => myChart.lineMinRight = v,
          () => myChart.lineMaxRight, (v: number) => myChart.lineMaxRight = v,
          '#e8eaee');
      }
    }

    // =========================================================
    // drawChart() — called on init and on every brush event
    // =========================================================
    function drawChart(): void {
      // Axis styling
      mySvg.selectAll('#xAxis' + myClass + ' line')
        .attr('y1', '0').attr('y2', -chartHeight)
        .style('stroke', '#F0F3F6').style('stroke-width', 1);

      mySvg.selectAll('.axis' + myClass + ' text')
        .style('font-weight', myChart.props.axisFontWeight)
        .style('font-family', 'Poppins')
        .style('font-size', myChart.props.axisFontSize);

      mySvg.selectAll('#xAxis' + myClass + ' text')
        .style('text-anchor', 'middle')
        .attr('dx', -5).attr('dy', 5)
        .attr('transform', '');

      mySvg.selectAll('#yAxis' + myClass + ' line')
        .attr('x1', '0').attr('x2', plotWidth)
        .style('stroke', '#F0F3F6').style('stroke-width', 1);

      if (hasDualAxis) {
        mySvg.selectAll('#yAxisRight' + myClass + ' line').style('stroke', 'transparent');
      }

      // Tick overlap detection
      const xBandwidth = dateGroupDates.length > 1 ? xScale(dateGroupDates[1]) - xScale(dateGroupDates[0]) : 0;
      const maxTextWidth = getMaxTextWidth();
      if (maxTextWidth > (xBandwidth - 5)) {
        let myFontSize = xBandwidth < myChart.props.axisFontSize + 2 ? xBandwidth - 2 : myChart.props.axisFontSize;
        mySvg.selectAll('#xAxis' + myClass + ' text')
          .style('text-anchor', (myChart.props.d3AxisFormatting === true ? 'middle' : 'end'))
          .attr('dy', (myChart.props.d3AxisFormatting === true ? 5 : 1))
          .style('font-size', myFontSize)
          .style('font-family', 'Poppins')
          .attr('transform', 'rotate(' + (myChart.props.d3AxisFormatting === true ? 0 : -45) + ')');
      }

      // Brush handle lines
      if (myChart.props.showBrush === true) {
        const handleEastX = +mySvg.select('#brushGroup' + myClass).select('.handle--e').attr('x');
        const handleWestX = +mySvg.select('#brushGroup' + myClass).select('.handle--w').attr('x');
        mySvg.select('.handleLeftLine1' + myClass).attr('x1', handleEastX + 3).attr('x2', handleEastX + 3);
        mySvg.select('.handleLeftLine2' + myClass).attr('x1', handleEastX + 7).attr('x2', handleEastX + 7);
        mySvg.select('.handleRightLine1' + myClass).attr('x1', handleWestX + 3).attr('x2', handleWestX + 3);
        mySvg.select('.handleRightLine2' + myClass).attr('x1', handleWestX + 7).attr('x2', handleWestX + 7);
        mySvg.select('#brushGroup' + myClass)
          .selectAll('.handle')
          .attr('fill', 'white')
          .attr('rx', 4).attr('ry', 4)
          .attr('y', (margins.brush - 24) / 2)
          .attr('height', 24)
          .style('filter', 'url(#drop-shadow-' + myClass + ')');
      }

      // Partial period line
      mySvg.select('.partialPeriodLine' + myClass)
        .attr('visibility', myChart.props.partialPeriod === true ? 'visible' : 'hidden')
        .attr('x1', margins.left + xScale(xMax))
        .attr('x2', margins.left + xScale(xMax))
        .attr('y1', margins.top)
        .attr('y2', chartHeight + margins.top)
        .attr('stroke', '#1363DF')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '2,2');

      mySvg.select('.partialPeriodLineLabel' + myClass)
        .attr('visibility', myChart.props.partialPeriod === true ? 'visible' : 'hidden')
        .attr('transform', 'translate(' + (margins.left + xScale(xMax)) + ',' + (margins.top - 12) + ')')
        .attr('text-anchor', 'middle')
        .attr('dy', 0)
        .attr('font-size', '8px')
        .attr('font-family', 'Poppins')
        .attr('font-weight', '400')
        .attr('fill', '#1363DF')
        .text('Partial period')
        .call(wrap, 35);

      mySvg.selectAll('.selection')
        .attr('visibility', chartData.length === 0 ? 'hidden' : 'visible');

      myChart.currentVisibleData = [chartData, xScale.domain()];

      // ---- MAIN CHART GROUP ----
      const chartGroup = mySvg.select('#chartGroup' + myClass)
        .selectAll('.chartGroup' + myClass)
        .data(chartGroupData)
        .join((group: any) => {
          const enter = group.append('g').attr('class', 'chartGroup' + myClass);
          // 1. Area fills
          areaKeys.forEach((key: string) => {
            enter.append('path').attr('class', 'areaPath areaPath_' + key);
          });
          // 2. Bars group
          enter.append('g').attr('class', 'barsGroup');
          // 3. Area stroke line
          areaKeys.forEach((key: string) => {
            enter.append('path').attr('class', 'areaStrokePath areaStrokePath_' + key);
          });
          // 4. Line paths
          lineScaleKeys.forEach((key: string) => {
            if (areaKeys.indexOf(key) === -1) {
              enter.append('path').attr('class', 'linePath linePath_' + key);
            }
          });
          // 5. Line dots
          lineScaleKeys.forEach((key: string) => {
            enter.append('g').attr('class', 'lineDotsGroup lineDotsGroup_' + key);
          });
          // 6. Data labels
          enter.append('g').attr('class', 'barLabelsGroup');
          lineScaleKeys.forEach((key: string) => {
            enter.append('g').attr('class', 'lineLabelsGroup lineLabelsGroup_' + key);
          });
          return enter;
        });

      chartGroup.attr('clip-path', (d: any) => d === 'brush' ? '' : 'url(#brushClip' + myClass + ')');

      // ---- AREA FILL ----
      areaKeys.forEach((key: string, ki: number) => {
        const hidden = myChart.hiddenKeys.has(key);
        const targetOpacity = hidden ? 0 : (isStackedArea ? 1 : 0.3);
        chartGroup.select('.areaPath_' + key)
          .attr('fill', myChart.props.colours[key])
          .attr('stroke', 'none')
          .attr('opacity', targetOpacity)
          // Area paths never catch pointer events — the hover overlay rect
          // (see "UNIVERSAL HOVER OVERLAY" below) handles hover for areas.
          // This is required in crosshairMode='hover' so that, after the
          // overlay is lowered beneath bars/dots, mousemove over an area fill
          // still reaches the overlay instead of being swallowed by the area.
          .style('pointer-events', 'none')
          .attr('d', (d: any) => d === 'brush' ? areaGeneratorsBrush[key](chartData) : areaGenerators[key](chartData))
          .attr('transform', (d: any) => 'translate(' + margins.left + ',' + (d === 'brush' ? (margins.mid + chartHeight + margins.top) : margins.top) + ')')
          .each(function (this: any, d: any) {
            if (isInitialRender && myChart.props.animate && d !== 'brush' && !hidden) {
              const el = d3.select(this);
              const totalLength = (el.node() as SVGPathElement).getTotalLength();
              el.attr('stroke-dasharray', totalLength)
                .attr('stroke-dashoffset', totalLength)
                .attr('stroke', myChart.props.colours[key])
                .attr('stroke-width', 1)
                .attr('opacity', 0)
                .transition().duration(800).delay(ki * 100).ease(d3.easeQuadOut)
                .attr('stroke-dashoffset', 0)
                .attr('opacity', targetOpacity)
                .on('end', function () {
                  d3.select(this).attr('stroke', 'none').attr('stroke-dasharray', null).attr('stroke-dashoffset', null);
                });
            }
          });
      });

      // ---- AREA STROKE LINE ----
      areaKeys.forEach((key: string) => {
        if (isStackedArea && lineGenerators[key]) {
          chartGroup.select('.areaStrokePath_' + key)
            .style('stroke', 'white')
            .style('fill', 'transparent')
            .style('stroke-width', 1.5)
            .style('pointer-events', 'none')
            .attr('d', (d: any) => d === 'brush' ? lineGeneratorsBrush[key](chartData) : lineGenerators[key](chartData))
            .attr('transform', (d: any) => 'translate(' + margins.left + ',' + (d === 'brush' ? (margins.mid + chartHeight + margins.top) : margins.top) + ')');
        } else {
          chartGroup.select('.areaStrokePath_' + key)
            .style('stroke', 'none')
            .style('fill', 'transparent');
        }
      });

      // ---- LINE RENDERING ----
      lineScaleKeys.forEach((key: string) => {
        if (areaKeys.indexOf(key) === -1) {
          const hidden = myChart.hiddenKeys.has(key);
          chartGroup.select('.linePath_' + key)
            .style('stroke', myChart.props.colours[key])
            .style('fill', 'transparent')
            .style('stroke-width', 3)
            .style('opacity', hidden ? 0 : 1)
            .attr('d', (d: any) => d === 'brush' ? lineGeneratorsBrush[key](chartData) : lineGenerators[key](chartData))
            .attr('transform', (d: any) => 'translate(' + margins.left + ',' + (d === 'brush' ? (margins.mid + chartHeight + margins.top) : margins.top) + ')')
            .each(function (this: any, d: any) {
              if (isInitialRender && myChart.props.animate && d !== 'brush' && !hidden) {
                const path = d3.select(this);
                const totalLength = (path.node() as SVGPathElement).getTotalLength();
                path.attr('stroke-dasharray', totalLength)
                  .attr('stroke-dashoffset', totalLength)
                  .transition().duration(800).ease(d3.easeQuadOut)
                  .attr('stroke-dashoffset', 0);
              }
            });
        }
      });

      // ---- LINE DOTS ----
      lineScaleKeys.forEach((key: string) => {
        const dotsGroup = chartGroup.select('.lineDotsGroup_' + key);
        if (!dotShow || areaKeys.indexOf(key) > -1 || myChart.hiddenKeys.has(key)) {
          dotsGroup.selectAll('*').remove();
          return;
        }
        dotsGroup.attr('transform', (d: any) => 'translate(0,' + (d === 'brush' ? (margins.mid + chartHeight) : 0) + ')');

        const dots = dotsGroup.selectAll('.lineDot_' + key)
          .data((d: any) => {
            const myData = JSON.parse(JSON.stringify(chartData));
            myData.map((n: any) => { n.date = new Date(n.date); n.type = d; });
            return myData.filter((f: any) => f[key] !== null && f[key] !== undefined);
          })
          .join((enter: any) => {
            return enter.append('circle').attr('class', 'lineDot_' + key);
          });

        dots
          .attr('cx', (d: any) => d.type === 'brush' ? xScaleAll(d.date) : xScale(d.date))
          .attr('cy', (d: any) => d.type === 'brush' ? yLineScaleBrush(d[key]) : yLineScale(d[key]))
          .attr('fill', dotFill)
          .style('stroke', myChart.props.colours[key])
          .style('stroke-width', dotStrokeWidth)
          .each(function (this: any, d: any) {
            const dot = d3.select(this);
            const targetR = d.type === 'brush' ? 2 : circleRadius;
            if (isInitialRender && myChart.props.animate && d.type !== 'brush') {
              dot.attr('r', 0).transition().duration(400).delay(600).attr('r', targetR);
            } else {
              dot.attr('r', targetR);
            }
          })
          .attr('transform', 'translate(' + margins.left + ',' + margins.top + ')');
      });

      // ---- BARS RENDERING ----
      chartGroup.select('.barsGroup')
        .attr('transform', (d: any) => 'translate(0,' + (d === 'brush' ? (margins.mid + chartHeight) : 0) + ')');

      const barGroup = chartGroup.select('.barsGroup')
        .selectAll('.barDateGroup')
        .data((d: any) => {
          const myData = JSON.parse(JSON.stringify(chartData));
          myData.map((n: any) => { n.date = new Date(n.date); n.type = d; });
          return myData;
        })
        .join((group: any) => {
          const enter = group.append('g').attr('class', 'barDateGroup');
          barKeys.forEach((key: string) => {
            enter.append('path').attr('class', 'barRect barRect_' + key);
          });
          return enter;
        });

      // Calculate bar widths
      const barPadding = 2;
      const numBarsPerGroup = barMode === 'clustered' ? barKeys.length : 1;

      barKeys.forEach((key: string, ki: number) => {
        const barHidden = myChart.hiddenKeys.has(key);
        barGroup.select('.barRect_' + key).each(function (this: any, d: any) {
          const el = d3.select(this);
          if (barHidden) { el.attr('d', '').attr('opacity', 0); return; }
          const myXScale = d.type === 'brush' ? xScaleAll : xScale;
          const myYScale = d.type === 'brush' ? yScaleBrush : yScale;
          const bw = dateGroupDates.length > 1
            ? Math.abs(myXScale(dateGroupDates[1]) - myXScale(dateGroupDates[0]))
            : plotWidth / 2;
          const maxBarWidth = Math.max(2, (bw * 0.7) / numBarsPerGroup - barPadding);

          // barRadius props: { value: 4, mode: 'all' | 'top' | 'none' | 'eachTop' }
          const brProps = myChart.props.barRadius || { value: 4, mode: 'eachTop' };
          const brMode = brProps.mode || 'eachTop';
          const brValue = brProps.value != null ? brProps.value : 4;
          const isTopBar = ki === barKeys.length - 1;

          // helper: build SVG path for a rect with per-corner radii
          const rrPath = (bx: number, by: number, bw2: number, bh2: number, rtl: number, rtr: number, rbr2: number, rbl: number) => {
            if (bw2 <= 0) bw2 = 0;
            if (bh2 <= 0) bh2 = 0;
            rtl = bh2 > 0 ? Math.min(rtl, bw2 / 2, bh2 / 2) : 0;
            rtr = bh2 > 0 ? Math.min(rtr, bw2 / 2, bh2 / 2) : 0;
            rbr2 = bh2 > 0 ? Math.min(rbr2, bw2 / 2, bh2 / 2) : 0;
            rbl = bh2 > 0 ? Math.min(rbl, bw2 / 2, bh2 / 2) : 0;
            return `M${bx + rtl},${by}`
              + `H${bx + bw2 - rtr}`
              + `A${rtr},${rtr},0,0,1,${bx + bw2},${by + rtr}`
              + `V${by + bh2 - rbr2}`
              + `A${rbr2},${rbr2},0,0,1,${bx + bw2 - rbr2},${by + bh2}`
              + `H${bx + rbl}`
              + `A${rbl},${rbl},0,0,1,${bx},${by + bh2 - rbl}`
              + `V${by + rtl}`
              + `A${rtl},${rtl},0,0,1,${bx + rtl},${by}`
              + 'Z';
          };

          // resolve per-corner radii based on mode
          const isStacked = barMode !== 'clustered';
          const resolveRadii = (r: number, mode: string, top: boolean) => {
            if (mode === 'none' || r <= 0) return [0, 0, 0, 0];
            if (mode === 'all') return [r, r, r, r];
            if (mode === 'top' && top) return [r, r, 0, 0];
            if (mode === 'top' && !top) return [0, 0, 0, 0];
            if (mode === 'eachTop') return (isStacked && !top) ? [0, 0, 0, 0] : [r, r, 0, 0];
            return [0, 0, 0, 0];
          };

          if (barMode === 'clustered') {
            const groupWidth = maxBarWidth * numBarsPerGroup + barPadding * (numBarsPerGroup - 1);
            const barX = myXScale(d.date) - groupWidth / 2 + ki * (maxBarWidth + barPadding);
            const barY = myYScale(d[key]);
            const barH = Math.max(0, myYScale(0) - myYScale(d[key]));
            const rMax = d.type === 'brush' ? Math.min(2, brValue) : brValue;
            const r = Math.min(rMax, maxBarWidth / 2, barH / 2);
            const [rtl, rtr, rbr, rbl] = resolveRadii(r, brMode, isTopBar);
            el.attr('fill', myChart.props.colours[key])
              .attr('transform', 'translate(' + margins.left + ',' + margins.top + ')');
            if (isInitialRender && myChart.props.animate && d.type !== 'brush') {
              el.attr('d', rrPath(barX, myYScale(0), maxBarWidth, 0, rtl, rtr, rbr, rbl))
                .transition().duration(600).delay(ki * 60 + 100).ease(d3.easeCubicOut)
                .attr('d', rrPath(barX, barY, maxBarWidth, barH, rtl, rtr, rbr, rbl));
            } else {
              el.attr('d', rrPath(barX, barY, maxBarWidth, barH, rtl, rtr, rbr, rbl));
            }
          } else {
            // Stacked
            const stackBarWidth = Math.max(2, bw * 0.45);
            const barX = myXScale(d.date) - stackBarWidth / 2;
            const barY = myYScale(d[key + '_y1']);
            const barH = Math.max(0, myYScale(d[key + '_y0']) - myYScale(d[key + '_y1']));
            const rMax = d.type === 'brush' ? Math.min(2, brValue) : brValue;
            const r = Math.min(rMax, stackBarWidth / 2, barH / 2);
            const [rtl, rtr, rbr, rbl] = resolveRadii(r, brMode, isTopBar);
            el.attr('fill', myChart.props.colours[key])
              .attr('transform', 'translate(' + margins.left + ',' + margins.top + ')');
            if (isInitialRender && myChart.props.animate && d.type !== 'brush') {
              el.attr('d', rrPath(barX, myYScale(d[key + '_y0']), stackBarWidth, 0, rtl, rtr, rbr, rbl))
                .transition().duration(600).delay(ki * 80 + 100).ease(d3.easeCubicOut)
                .attr('d', rrPath(barX, barY, stackBarWidth, barH, rtl, rtr, rbr, rbl));
            } else {
              el.attr('d', rrPath(barX, barY, stackBarWidth, barH, rtl, rtr, rbr, rbl));
            }
          }
        });
      });

      // ---- BAR DATA LABELS ----
      const barLabelsGroup = chartGroup.select('.barLabelsGroup');
      barLabelsGroup
        .attr('transform', (d: any) => 'translate(0,' + (d === 'brush' ? (margins.mid + chartHeight) : 0) + ')');
      barLabelsGroup.selectAll('.barLabel').remove();
      barGroup.selectAll('.barLabel').remove();
      if (dlShowBar && barKeys.length > 0) {
        barGroup.each(function (this: any, d: any) {
          if (d.type === 'brush') return;
          const parent = d3.select(this);
          const myXScale = xScale;
          const myYScale = yScale;
          const bw = dateGroupDates.length > 1
            ? Math.abs(myXScale(dateGroupDates[1]) - myXScale(dateGroupDates[0]))
            : plotWidth / 2;

          if (barMode === 'clustered') {
            const maxBarWidth = Math.max(2, (bw * 0.7) / numBarsPerGroup - barPadding);
            const groupWidth = maxBarWidth * numBarsPerGroup + barPadding * (numBarsPerGroup - 1);
            let clusterTotal = 0;
            let clusterTopY = Infinity;
            barKeys.forEach((key: string, ki: number) => {
              if (myChart.hiddenKeys.has(key) || d[key] === null || d[key] === undefined) return;
              const barX = myXScale(d.date) - groupWidth / 2 + ki * (maxBarWidth + barPadding) + maxBarWidth / 2;
              const barY = myYScale(d[key]);
              const barBottom = myYScale(0);
              const barH = barBottom - barY;
              clusterTotal += d[key];
              if (barY < clusterTopY) clusterTopY = barY;
              if (dlShowValue && barH >= dlFontSize + 2) {
                const barCenterY = barY + barH / 2;
                parent.append('text').attr('class', 'barLabel')
                  .attr('x', barX + margins.left)
                  .attr('y', barCenterY + margins.top + dlFontSize / 3)
                  .attr('text-anchor', 'middle')
                  .attr('fill', dlColor)
                  .attr('font-family', 'Poppins')
                  .attr('font-size', dlFontSize + 'px')
                  .attr('font-weight', dlFontWeight)
                  .text(d3.format(dlFormat)(d[key]));
              }
            });
            if (dlShowTotal && clusterTotal > 0) {
              parent.append('text').attr('class', 'barLabel')
                .attr('x', myXScale(d.date) + margins.left)
                .attr('y', clusterTopY + margins.top - 5)
                .attr('text-anchor', 'middle')
                .attr('fill', dlTotalColor)
                .attr('font-family', 'Poppins')
                .attr('font-size', dlFontSize + 'px')
                .attr('font-weight', dlTotalFontWeight)
                .text(d3.format(dlFormat)(clusterTotal));
            }
          } else {
            // Stacked labels
            let stackTotal = 0;
            let stackTopY = Infinity;
            barKeys.forEach((key: string) => {
              if (myChart.hiddenKeys.has(key) || d[key + '_y1'] == null || d[key] === null || d[key] === undefined || d[key] === 0) return;
              const barX = myXScale(d.date);
              const barTop = myYScale(d[key + '_y1']);
              const barBottom = myYScale(d[key + '_y0']);
              const barH = barBottom - barTop;
              stackTotal += d[key];
              if (barTop < stackTopY) stackTopY = barTop;
              if (dlShowValue && barH >= dlFontSize + 2) {
                const barCenterY = barTop + barH / 2;
                parent.append('text').attr('class', 'barLabel')
                  .attr('x', barX + margins.left)
                  .attr('y', barCenterY + margins.top + dlFontSize / 3)
                  .attr('text-anchor', 'middle')
                  .attr('fill', dlColor)
                  .attr('font-family', 'Poppins')
                  .attr('font-size', dlFontSize + 'px')
                  .attr('font-weight', dlFontWeight)
                  .text(d3.format(dlFormat)(d[key]));
              }
            });
            if (dlShowTotal && stackTotal > 0) {
              parent.append('text').attr('class', 'barLabel')
                .attr('x', myXScale(d.date) + margins.left)
                .attr('y', stackTopY + margins.top - 5)
                .attr('text-anchor', 'middle')
                .attr('fill', dlTotalColor)
                .attr('font-family', 'Poppins')
                .attr('font-size', dlFontSize + 'px')
                .attr('font-weight', dlTotalFontWeight)
                .text(d3.format(dlFormat)(stackTotal));
            }
          }
        });
      }

      // ---- LINE DATA LABELS ----
      lineScaleKeys.forEach((key: string) => {
        const labelsGroup = chartGroup.select('.lineLabelsGroup_' + key);
        labelsGroup.selectAll('.lineLabel').remove();
        if (!dlShowLine || areaKeys.indexOf(key) > -1 || myChart.hiddenKeys.has(key)) return;
        labelsGroup.attr('transform', (d: any) => 'translate(0,' + (d === 'brush' ? (margins.mid + chartHeight) : 0) + ')');

        const labelData = chartData.filter((f: any) => f[key] !== null && f[key] !== undefined);
        labelsGroup.selectAll('.lineLabel_' + key)
          .data((d: any) => {
            const myData = JSON.parse(JSON.stringify(labelData));
            myData.map((n: any) => { n.date = new Date(n.date); n.type = d; });
            return myData;
          })
          .join((enter: any) => enter.append('text').attr('class', 'lineLabel lineLabel_' + key))
          .attr('x', (d: any) => (d.type === 'brush' ? xScaleAll(d.date) : xScale(d.date)) + margins.left)
          .attr('y', (d: any) => (d.type === 'brush' ? yLineScaleBrush(d[key]) : yLineScale(d[key])) + margins.top - circleRadius - 5)
          .attr('text-anchor', 'middle')
          .attr('fill', dlColor)
          .attr('font-family', 'Poppins')
          .attr('font-size', (d: any) => (d.type === 'brush' ? 0 : dlFontSize) + 'px')
          .attr('font-weight', dlFontWeight)
          .text((d: any) => d3.format(dlFormat)(d[key]));
      });

      // ---- AREA DATA LABELS ----
      mySvg.selectAll('.areaLabel').remove();
      if (dlShowArea && areaKeys.length > 0) {
        chartData.forEach((d: any) => {
          if (isStackedArea) {
            let areaTotal = 0;
            let areaTopY = Infinity;
            areaKeys.forEach((key: string) => {
              if (myChart.hiddenKeys.has(key) || d[key + '_y1'] == null || d[key] === null || d[key] === undefined || d[key] === 0) return;
              const areaTop = yLineScale(d[key + '_y1']);
              const areaBottom = yLineScale(d[key + '_y0']);
              const areaH = areaBottom - areaTop;
              areaTotal += d[key];
              if (areaTop < areaTopY) areaTopY = areaTop;
              if (dlShowValue && areaH >= dlFontSize + 2) {
                const areaCenterY = areaTop + areaH / 2;
                mySvg.append('text').attr('class', 'areaLabel')
                  .attr('x', xScale(d.date) + margins.left)
                  .attr('y', areaCenterY + margins.top + dlFontSize / 3)
                  .attr('text-anchor', 'middle')
                  .attr('fill', dlColor)
                  .attr('font-family', 'Poppins')
                  .attr('font-size', dlFontSize + 'px')
                  .attr('font-weight', dlFontWeight)
                  .text(d3.format(dlFormat)(d[key]));
              }
            });
            if (dlShowTotal && areaTotal > 0) {
              mySvg.append('text').attr('class', 'areaLabel')
                .attr('x', xScale(d.date) + margins.left)
                .attr('y', areaTopY + margins.top - 5)
                .attr('text-anchor', 'middle')
                .attr('fill', dlTotalColor)
                .attr('font-family', 'Poppins')
                .attr('font-size', dlFontSize + 'px')
                .attr('font-weight', dlTotalFontWeight)
                .text(d3.format(dlFormat)(areaTotal));
            }
          } else {
            areaKeys.forEach((key: string) => {
              if (myChart.hiddenKeys.has(key) || d[key] === null || d[key] === undefined) return;
              mySvg.append('text').attr('class', 'areaLabel')
                .attr('x', xScale(d.date) + margins.left)
                .attr('y', yLineScale(d[key]) + margins.top - 5)
                .attr('text-anchor', 'middle')
                .attr('fill', dlColor)
                .attr('font-family', 'Poppins')
                .attr('font-size', dlFontSize + 'px')
                .attr('font-weight', dlFontWeight)
                .text(d3.format(dlFormat)(d[key]));
            });
          }
        });
      }

      // ---- HOVER LINE setup ----
      const hoverLineProps = myChart.props.hoverLine || { show: false };
      const hoverLine = mySvg.select('#hoverLine' + myClass)
        .attr('y1', margins.top)
        .attr('y2', margins.top + chartHeight)
        .attr('stroke', hoverLineProps.colour || '#B0B0B0')
        .attr('stroke-width', hoverLineProps.width || 1)
        .attr('stroke-dasharray', hoverLineProps.dash || '4,3')
        .attr('pointer-events', 'none')
        .style('opacity', 0);

      // ---- CROSSHAIR GROUP (horizontal dotted lines + value labels) ----
      const crosshairGroup = mySvg.select('#hoverCrosshairGroup' + myClass);
      crosshairGroup.selectAll('*').remove();
      crosshairGroup.style('opacity', 0);

      // crosshair prop: controls which hover types show horizontal guide lines
      // e.g. { bar: true, line: true, area: false }
      const crosshairProps = myChart.props.crosshair || { bar: true, line: true, area: true };
      // crosshairMode:
      //   'all'   → full-plot overlay; hovering anywhere draws a crosshair for
      //             every visible series at that x. Unified tooltip. (default)
      //   'hover' → crosshair only when the mouse is directly over a bar, line
      //             dot, or area — and shows only that single hovered series.
      const crosshairMode: 'hover' | 'all' = myChart.props.crosshairMode === 'hover' ? 'hover' : 'all';

      const hoverSuffix = myChart.props.yAxisSuffix || '';

      // determine which keys use right axis
      const rightAxisKeySet = new Set(
        (isStackedArea || lineOnPrimary) ? [] : [...lineKeys, ...areaKeys.filter((k: string) => lineKeys.indexOf(k) === -1)]
      );

      // formats a number the same way the crosshair label does
      function formatCrosshairValue(val: number): string {
        return (val < 1 && val > 0 ? val.toFixed(1) : (Number.isInteger(val) ? d3.format(',')(val) : d3.format(',.1f')(val))) + hoverSuffix;
      }

      // helper: draw a single horizontal dotted line from data point to axis with value label.
      // `val` is the value the Y line is drawn at (for stacked bars this is the cumulative
      // stack top); `segmentVal` is the optional raw segment value shown in parens before it,
      // so a stacked bar segment of 300 sitting at axis value 4500 reads "(300) 4,500".
      function drawCrosshair(
        xPos: number,
        val: number | null | undefined,
        yScaleUsed: any,
        colour: string,
        side: 'left' | 'right',
        segmentVal?: number | null,
      ): void {
        if (val === null || val === undefined || isNaN(val)) return;
        const yPos = yScaleUsed(val) + margins.top;
        const axisX = side === 'left' ? margins.left : margins.left + plotWidth;

        // horizontal dotted line from axis to data point x
        crosshairGroup.append('line')
          .attr('x1', axisX)
          .attr('y1', yPos)
          .attr('x2', xPos)
          .attr('y2', yPos)
          .attr('stroke', colour)
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '4,3')
          .attr('pointer-events', 'none');

        // value label at the axis end — prepend segment value in parens when
        // it differs from the stacked total (stacked bars only).
        const totalFormatted = formatCrosshairValue(val);
        const showSegment =
          segmentVal !== undefined &&
          segmentVal !== null &&
          !isNaN(segmentVal) &&
          segmentVal !== val;
        const formatted = showSegment
          ? `(${formatCrosshairValue(segmentVal as number)}) ${totalFormatted}`
          : totalFormatted;

        // Push 9px outward from the axis (4px base + 5px extra) so the
        // tooltip pill clears the brush/range slider on either side.
        const labelX = side === 'left' ? axisX - 9 : axisX + 9;
        const anchor = side === 'left' ? 'end' : 'start';

        const textNode = crosshairGroup.append('text')
          .attr('x', labelX)
          .attr('y', yPos + 4)
          .attr('text-anchor', anchor)
          .attr('font-family', 'Poppins, sans-serif')
          .attr('font-size', '10px')
          .attr('font-weight', '600')
          .attr('fill', '#ffffff')
          .attr('pointer-events', 'none')
          .text(formatted);

        // bg rect behind the label — tooltip-style black pill with white text
        const bbox = (textNode.node() as SVGTextElement).getBBox();
        const padX = 6;
        const padY = 3;
        crosshairGroup.insert('rect', 'text:last-of-type')
          .attr('x', bbox.x - padX)
          .attr('y', bbox.y - padY)
          .attr('width', bbox.width + padX * 2)
          .attr('height', bbox.height + padY * 2)
          .attr('rx', 3)
          .attr('fill', '#000000')
          .attr('opacity', 0.8)
          .attr('pointer-events', 'none');
      }

      // hoverType: 'bar' | 'line' | 'area' | 'chart'
      //   'chart' = universal hover from the full-plot overlay; draws a
      //   crosshair for every visible series at the hovered x.
      // hoveredKey: specific key being hovered (e.g. from bar class or line dot class)
      function showHoverLine(d: any, hoverType?: string, hoveredKey?: string): void {
        if (!hoverLineProps.show || !d || !d.date) return;
        const scale = d.type === 'brush' ? xScaleAll : xScale;
        const xPos = scale(new Date(d.date)) + margins.left;
        hoverLine.attr('x1', xPos).attr('x2', xPos)
          .style('opacity', 1);

        // clear previous crosshair elements
        crosshairGroup.selectAll('*').remove();

        // check if crosshair is enabled for this hover type
        const crosshairEnabled = hoverType === 'chart'
          ? !!(crosshairProps.bar || crosshairProps.line || crosshairProps.area)
          : !!(hoverType && crosshairProps[hoverType]);
        if (!hoverType || !crosshairEnabled) {
          crosshairGroup.style('opacity', 0);
          return;
        }

        // helpers for drawing each series type
        const drawBarKey = (key: string): void => {
          const val = d[key];
          if (val === null || val === undefined || isNaN(val)) return;
          const isRight = hasDualAxis && rightAxisKeySet.has(key);
          const yS = isRight ? yScaleRight : yScale;
          const isStackedBar = barMode === 'stacked' && d[key + '_y1'] != null;
          const yVal = isStackedBar ? d[key + '_y1'] : val;
          const segmentVal = isStackedBar ? val : undefined;
          drawCrosshair(xPos, yVal, yS, myChart.props.colours[key] || '#333', isRight ? 'right' : 'left', segmentVal);
        };
        const drawLineKey = (key: string): void => {
          const val = d[key];
          if (val === null || val === undefined || isNaN(val)) return;
          const isRight = hasDualAxis && rightAxisKeySet.has(key);
          drawCrosshair(xPos, val, isRight ? yScaleRight : yScale, myChart.props.colours[key] || '#333', isRight ? 'right' : 'left');
        };
        const drawAreaKey = (key: string): void => {
          const isRight = hasDualAxis && rightAxisKeySet.has(key);
          const yS = isRight ? yScaleRight : yScale;
          const yVal = (isStackedArea && d[key + '_y1'] != null) ? d[key + '_y1'] : d[key];
          if (yVal === null || yVal === undefined || isNaN(yVal)) return;
          drawCrosshair(xPos, yVal, yS, myChart.props.colours[key] || '#333', isRight ? 'right' : 'left');
        };

        if (hoverType === 'bar' && hoveredKey) {
          drawBarKey(hoveredKey);
        } else if (hoverType === 'line' && hoveredKey) {
          drawLineKey(hoveredKey);
        } else if (hoverType === 'area') {
          const areaOnlyKeysLocal = areaKeys.filter((k: string) => lineKeys.indexOf(k) === -1);
          areaOnlyKeysLocal.forEach(drawAreaKey);
        } else if (hoverType === 'chart') {
          // universal hover — draw crosshair for every visible series
          if (crosshairProps.bar !== false)  barKeys.forEach(drawBarKey);
          if (crosshairProps.line !== false) lineKeys.forEach(drawLineKey);
          if (crosshairProps.area !== false) {
            const areaOnlyKeysLocal = areaKeys.filter((k: string) => lineKeys.indexOf(k) === -1);
            areaOnlyKeysLocal.forEach(drawAreaKey);
          }
        } else {
          // fallback: show all keys of the hover type
          if (hoverType === 'bar')  barKeys.forEach(drawBarKey);
          else if (hoverType === 'line') lineKeys.forEach(drawLineKey);
        }

        crosshairGroup.style('opacity', 1);
      }
      function hideHoverLine(): void {
        hoverLine.style('opacity', 0);
        crosshairGroup.style('opacity', 0);
      }

      // ---- MOUSEOVER (bars + dots) ----
      mySvg.selectAll('.barRect')
        .on('mouseover', function (this: any, event: any, d: any) {
          if (d && d.type !== 'brush') {
            // detect which bar key from the element's class (barRect_<key>)
            const classList = (this.getAttribute('class') || '');
            let hoveredKey = '';
            barKeys.forEach((key: string) => {
              if (classList.indexOf('barRect_' + key) !== -1) hoveredKey = key;
            });
            showHoverLine(d, 'bar', hoveredKey);
            myChart.showTooltip('bar', d, event.pageX, event.pageY, width);
          }
        })
        .on('mouseout', () => { hideHoverLine(); myChart.hideTooltip('bar'); })
        .on('click', (event: any, d: any) => {
          if (d && d.type !== 'brush') {
            // Emit event for external listeners
            const xVar = myChart.props.xAxisVar || '';
            if (xVar && d[xVar] !== undefined) {
              myChart.barClicked.emit({ column: xVar, value: d[xVar] });
            }
            // Enter waterfall drill-down (only if barKeys > 1)
            if (barKeys.length > 1) {
              myChart.hideTooltip('bar');
              hideHoverLine();
              myChart.enterWaterfallMode(d);
            }
          }
        });

      mySvg.selectAll('[class*="lineDot_"]')
        .on('mouseover', function (this: any, event: any, d: any) {
          if (d && d.type !== 'brush') {
            // detect which line key from the element's class (lineDot_<key>)
            const classList = (this.getAttribute('class') || '');
            let hoveredKey = '';
            lineKeys.forEach((key: string) => {
              if (classList.indexOf('lineDot_' + key) !== -1) hoveredKey = key;
            });
            showHoverLine(d, 'line', hoveredKey);
            myChart.showTooltip('lineDot', d, event.pageX, event.pageY, width);
          }
        })
        .on('mouseout', () => { hideHoverLine(); myChart.hideTooltip('lineDot'); });

      // ---- UNIVERSAL HOVER OVERLAY ----
      // A full-plot invisible rect catches mousemove everywhere (including the
      // gaps between bars and between line dots), so the crosshair + tooltip
      // work for every chart combination (bar-only, line-only, area-only,
      // bar+line, bar+area, line+area, bar+line+area). It draws a single
      // crosshair for *every* visible series via hoverType='chart'.
      //
      // In crosshairMode='hover' the overlay is only created when there are
      // area keys (legacy behavior) so that per-bar and per-line-dot mouseovers
      // can still fire natively and show a single-series crosshair.
      if (crosshairMode === 'all' || areaKeys.length > 0) {
        const bisectDate = d3.bisector((d: any) => d.date).left;
        // remove previous overlay if any
        mySvg.select('.areaHoverOverlay_' + myClass).remove();
        // select the 'chart' group (last .chartGroup), not the 'brush' group
        const allChartGroups = mySvg.selectAll('.chartGroup' + myClass);
        const chartGroupEl = d3.select(allChartGroups.nodes()[allChartGroups.size() - 1] as any);
        const overlayRect = chartGroupEl.append('rect')
          .attr('class', 'areaHoverOverlay_' + myClass)
          .attr('x', 0).attr('y', 0)
          .attr('width', plotWidth)
          .attr('height', chartHeight)
          .attr('transform', 'translate(' + margins.left + ',' + margins.top + ')')
          .style('fill', 'none')
          .style('pointer-events', 'all');

        // In 'hover' mode the overlay must sit BELOW bars and line dots so
        // that those elements receive their own native mouseover events and
        // fire single-series crosshairs. The overlay still catches mousemove
        // in the empty gaps (and over area fills, since area paths use
        // pointer-events:none). In 'all' mode we keep the overlay on top so
        // it blankets the whole plot and drives the unified crosshair.
        if (crosshairMode === 'hover') {
          overlayRect.lower();
        }

        overlayRect
          .on('mousemove', (event: any) => {
            const [mx] = d3.pointer(event, chartGroupEl.node());
            const xDate = xScale.invert(mx - margins.left);
            const idx = bisectDate(chartData, xDate, 1);
            const d0 = chartData[idx - 1];
            const d1 = chartData[idx];
            if (!d0 && !d1) return;
            const closest = !d1 ? d0 : !d0 ? d1 :
              (+xDate - +d0.date > +d1.date - +xDate ? d1 : d0);
            const hoverData = { ...closest, type: 'chart' };
            // 'all' → crosshair for every visible series at this x
            // 'hover' → overlay only exists for areas; draw single-series area crosshair (legacy)
            showHoverLine(hoverData, crosshairMode === 'all' ? 'chart' : 'area');
            myChart.showTooltip('area', hoverData, event.pageX, event.pageY, width);
          })
          .on('mouseout', () => {
            hideHoverLine();
            myChart.hideTooltip('area');
          })
          .on('click', (event: any) => {
            // preserve bar-click drilldown even though the overlay sits on top
            const [mx] = d3.pointer(event, chartGroupEl.node());
            const xDate = xScale.invert(mx - margins.left);
            const idx = bisectDate(chartData, xDate, 1);
            const d0 = chartData[idx - 1];
            const d1 = chartData[idx];
            if (!d0 && !d1) return;
            const closest = !d1 ? d0 : !d0 ? d1 :
              (+xDate - +d0.date > +d1.date - +xDate ? d1 : d0);
            const xVar = myChart.props.xAxisVar || '';
            if (xVar && closest && closest[xVar] !== undefined) {
              myChart.barClicked.emit({ column: xVar, value: closest[xVar] });
            }
            if (barKeys.length > 1 && closest) {
              myChart.hideTooltip('area');
              hideHoverLine();
              myChart.enterWaterfallMode(closest);
            }
          });
      }
    }

    // =========================================================
    // brushed()
    // =========================================================
    function brushed(event: any): void {
      if (!brushReady) return;
      const selection = event.selection;
      if (selection !== null) {
        myChart.hideTooltip('bar');
        const xDomain: any = selection.map(xScaleAll.invert);
        const dataRemaining: any = chartData.filter((f: any) => f.date >= xDomain[0] && f.date <= xDomain[1]);
        if (dataRemaining.length < 1) {
          const previousStart = xScaleAll(xScale.domain()[0]);
          const previousEnd = xScaleAll(xScale.domain()[1]);
          if (previousStart !== undefined && previousEnd !== undefined) {
            mySvg.select('#brushGroup' + myClass)
              //@ts-ignore
              .call(brush.move, myChart.props.showBrush === false ? null : [previousStart, previousEnd]);
          }
        } else {
          // Partial period visibility
          if (String(xDomain[1]) === String(xScaleAll.domain()[1])) {
            mySvg.select('.partialPeriodLine' + myClass)
              .attr('visibility', myChart.props.partialPeriod === true ? 'visible' : 'hidden');
            mySvg.select('.partialPeriodLineLabel' + myClass)
              .attr('visibility', myChart.props.partialPeriod === true ? 'visible' : 'hidden');
          } else {
            mySvg.select('.partialPeriodLine' + myClass).attr('visibility', 'hidden');
            mySvg.select('.partialPeriodLineLabel' + myClass).attr('visibility', 'hidden');
          }
          xScale.domain(xDomain);
          mySvg.select('#xAxis' + myClass)
            .call(
              d3.axisBottom(xScale)
                .tickSizeOuter(0).tickFormat(xTickFormat).ticks(tickCount)
                .tickValues(tickValues === null ? null : tickValues.filter((f: any) => f >= xDomain[0] && f <= xDomain[1])))
            .attr('transform', 'translate(' + margins.left + ',' + (margins.top + chartHeight) + ')');
          drawChart();
        }
      }
    }

    // =========================================================
    // Helper functions
    // =========================================================
    function wrap(text: any, width: any): void {
      text.selectAll('tspan').remove();
      let lineNumber = 0;
      const originalValue = text.text();
      text.each(function (): any {
        // @ts-ignore
        const text = d3.select(this);
        const words = text.text().split(/\s+/).reverse();
        let word = null;
        let line: any = [];
        const lineHeight = 1.1;
        const dy = 0;
        let tspan = text.text(null).append('tspan').attr('x', 0).attr('y', 0).attr('dy', dy + 'em');
        while (word = words.pop()) {
          line.push(word);
          tspan.text(line.join(' '));
          // @ts-ignore
          if (tspan.node().getComputedTextLength() > width) {
            line.pop();
            tspan.text(line.join(' '));
            line = [word];
            if (word.trim().length > 0) {
              tspan = text.append('tspan').attr('x', 0).attr('y', 0).attr('dy', ++lineNumber * lineHeight + dy + 'em').text(word);
            }
          }
        }
      });
      if (lineNumber > 1) {
        text.selectAll('tspan').remove();
        text.attr('id', originalValue);
        text.text(originalValue.substr(0, 5) + '..');
      }
    }

    function getMaxTextWidth(): number {
      let maxTextWidth = 0;
      mySvg.selectAll('#xAxis' + myClass + ' text').each(function () {
        // @ts-ignore
        const myNode = d3.select(this).node().getBBox();
        maxTextWidth = Math.max(maxTextWidth, myNode.width);
      });
      return maxTextWidth;
    }

    // =========================================================
    // D3 Range Slider Functions
    // =========================================================
    function createRangeSlider(
      id: string, min: number, max: number, side: string,
      targetScale: any, axisSelector: string,
      getMin: () => number, setMin: (v: number) => void,
      getMax: () => number, setMax: (v: number) => void,
      accentColour: string
    ): void {
      const sliderGroupId = 'sliderGroup-' + id;
      const sliderClass = id + '-slider';
      const xPos = side === 'left' ? margins.left : margins.left + plotWidth;

      slider.select('#' + sliderGroupId).remove();
      const sliderGroup = slider.append('g').attr('id', sliderGroupId);

      const scale = d3.scaleLinear()
        .domain([min, max])
        .range([targetScale.range()[0], 0])
        .clamp(true);

      const dragMinHandle = d3.drag().on('drag', (event: any) =>
        onDragHandle(sliderGroup, event, scale, sliderClass, id, min, max, 'min', side, targetScale, axisSelector, getMin, setMin, getMax, setMax));
      const dragMaxHandle = d3.drag().on('drag', (event: any) =>
        onDragHandle(sliderGroup, event, scale, sliderClass, id, min, max, 'max', side, targetScale, axisSelector, getMin, setMin, getMax, setMax));

      sliderGroup.append('line')
        .attr('class', sliderClass + ' ' + id + '-track')
        .attr('y1', scale(min)).attr('y2', scale(max))
        .attr('transform', 'translate(' + xPos + ',' + margins.top + ')')
        .attr('stroke', '#E8EAEE')
        .attr('stroke-width', 3)
        .attr('stroke-linecap', 'round');

      sliderGroup.append('line')
        .attr('class', sliderClass + ' ' + id + '-track-inset')
        .attr('y1', scale(getMin())).attr('y2', scale(getMax()))
        .attr('transform', 'translate(' + xPos + ',' + margins.top + ')')
        .attr('stroke', accentColour)
        .attr('stroke-width', 3)
        .attr('stroke-linecap', 'round');

      createSliderHandle(sliderGroup, sliderClass, id, 'min', getMin(), scale, dragMinHandle, xPos, accentColour);
      createSliderHandle(sliderGroup, sliderClass, id, 'max', getMax(), scale, dragMaxHandle, xPos, accentColour);
    }

    function createSliderHandle(sliderGroup: any, sliderClass: string, id: string, handleType: string, value: number, scale: any, dragHandle: any, xPos: number, accentColour: string): void {
      const hoverLayer = sliderGroup.append('circle')
        .attr('class', sliderClass + ' ' + id + '-' + handleType + '-hover-layer')
        .attr('cy', scale(value))
        .attr('transform', 'translate(' + xPos + ',' + margins.top + ')')
        .attr('r', myChart.handleRadius + 8)
        .attr('fill', accentColour)
        .attr('opacity', 0)
        .attr('pointer-events', 'none');

      const handle = sliderGroup.append('circle')
        .attr('class', sliderClass + ' ' + id + '-' + handleType + '-handle')
        .attr('cy', scale(value))
        .attr('transform', 'translate(' + xPos + ',' + margins.top + ')')
        .attr('r', myChart.handleRadius)
        .attr('fill', accentColour)
        .attr('stroke', 'white')
        .attr('stroke-width', 2)
        .style('cursor', 'ns-resize')
        .on('mousedown', function () { handle.raise(); })
        .call(dragHandle);

      handle.on('mouseover', function () {
        hoverLayer.transition().duration(200).attr('opacity', 0.4);
      })
      .on('mouseout', function () {
        hoverLayer.transition().duration(400).attr('opacity', 0);
      });
    }

    function onDragHandle(
      sliderGroup: any, event: any, scale: any, sliderClass: string, id: string,
      min: number, max: number, handleType: string, side: string,
      targetScale: any, axisSelector: string,
      getMin: () => number, setMin: (v: number) => void,
      getMax: () => number, setMax: (v: number) => void
    ): void {
      const pos = event.y - margins.top;
      const gap = Math.max(1, (max - min) * 0.05);

      if (handleType === 'min') {
        const clampedPos = Math.min(scale(min), Math.max(scale(max), pos));
        const newMin = scale.invert(clampedPos);
        if (newMin >= min && newMin <= getMax() - gap) {
          setMin(newMin);
          updateSliderHandlePosition(sliderGroup, sliderClass, id, 'min', clampedPos);
          updateTrackInset(sliderGroup, scale, sliderClass, id, newMin, getMax());
          targetScale.domain([newMin, getMax()]);
          updateAxis(targetScale, side, axisSelector);
          drawChart();
        }
      } else {
        const clampedPos = Math.max(scale(max), Math.min(scale(min), pos));
        const newMax = scale.invert(clampedPos);
        if (newMax <= max && newMax >= getMin() + gap) {
          setMax(newMax);
          updateSliderHandlePosition(sliderGroup, sliderClass, id, 'max', clampedPos);
          updateTrackInset(sliderGroup, scale, sliderClass, id, getMin(), newMax);
          targetScale.domain([getMin(), newMax]);
          updateAxis(targetScale, side, axisSelector);
          drawChart();
        }
      }
    }

    function updateAxis(targetScale: any, side: string, axisSelector: string): void {
      if (side === 'left') {
        mySvg.select(axisSelector)
          //@ts-ignore
          .call(d3.axisLeft(targetScale).tickSizeOuter(0).ticks(7)
            .tickFormat((d: any) => {
              return axisTickFormat(myChart.props.yAxisFormatting)(d) + (myChart.props.yAxisSuffix || '');
            }));
      } else {
        mySvg.select(axisSelector)
          .call(d3.axisRight(targetScale).tickSizeOuter(0).ticks(7)
            .tickFormat((d: any) => axisTickFormat(myChart.props.yAxisRightFormatting || '-2~s')(d) + (myChart.props.yAxisRightSuffix || '')));
      }
    }

    function updateTrackInset(sliderGroup: any, scale: any, sliderClass: string, id: string, min: number, max: number): void {
      sliderGroup.select('.' + sliderClass + '.' + id + '-track-inset')
        .attr('y1', scale(min)).attr('y2', scale(max));
    }

    function updateSliderHandlePosition(sliderGroup: any, sliderClass: string, id: string, handleType: string, newPos: number): void {
      sliderGroup.select('.' + sliderClass + '.' + id + '-' + handleType + '-handle').attr('cy', newPos);
      sliderGroup.select('.' + sliderClass + '.' + id + '-' + handleType + '-hover-layer').attr('cy', newPos);
    }
  }

  // ══════════════════════════════════════════════════════
  // WATERFALL DRILL-DOWN — In-place breakdown
  // ══════════════════════════════════════════════════════
  exitWaterfallMode(): void {
    this.waterfallActive = false;
    this.waterfallBarData = null;
    this.rebuildChart();
  }

  enterWaterfallMode(barData: any): void {
    this.waterfallActive = true;
    this.waterfallBarData = barData;
    this.plotWaterfallDrillDown();
  }

  private plotWaterfallDrillDown(): void {
    const myChart = this;
    const myClass = myChart.divId;
    const mySvg: any = d3.select('#svg_' + myClass);
    if (mySvg.empty()) return;

    const width: any = mySvg.node().getBoundingClientRect().width;
    const height = myChart.props.chartHeight;
    mySvg.attr('height', height);

    const barKeys = (myChart.props.barKeys || []).filter((k: string) => !myChart.hiddenKeys.has(k));
    const barData = myChart.waterfallBarData;
    if (!barData || barKeys.length === 0) return;

    const margins: any = { left: 75, right: 65, top: 60, bottom: 80, brush: 0, mid: 0 };
    const chartHeight = height - margins.top - margins.bottom;
    const plotWidth = width - margins.left - margins.right;

    // Build waterfall segments: Total → each key descending
    const total = barKeys.reduce((sum: number, key: string) => sum + (Number(barData[key]) || 0), 0);
    interface WfSegment {
      key: string;
      label: string;
      value: number;
      y0: number;
      y1: number;
      colour: string;
      isTotal: boolean;
    }
    const segments: WfSegment[] = [];

    // Total bar
    segments.push({
      key: '_total',
      label: 'TOTAL',
      value: total,
      y0: 0,
      y1: total,
      colour: '#1363DF',
      isTotal: true,
    });

    // Each barKey as waterfall cascade (descending from total)
    let running = total;
    barKeys.forEach((key: string) => {
      const val = Number(barData[key]) || 0;
      segments.push({
        key,
        label: (myChart.props.legendLabels[key] || key).toUpperCase(),
        value: val,
        y0: running - val,
        y1: running,
        colour: myChart.props.colours[key] || '#888',
        isTotal: false,
      });
      running -= val;
    });

    // ── Clear existing chart content ──
    mySvg.select('#chartGroup' + myClass).selectAll('*').remove();
    // Remove clip-path so waterfall bars are not clipped by the brush clip rect
    mySvg.select('#chartGroup' + myClass).attr('clip-path', null);
    mySvg.selectAll('.chartGroup' + myClass).attr('clip-path', null);
    mySvg.select('#brushGroup' + myClass).selectAll('*').remove();
    mySvg.selectAll('.handleLines' + myClass).attr('visibility', 'hidden');
    mySvg.select('#sliderGroup' + myClass).selectAll('*').remove();
    mySvg.select('#hoverLine' + myClass).style('opacity', 0);
    mySvg.select('.partialPeriodLine' + myClass).attr('visibility', 'hidden');
    mySvg.select('.partialPeriodLineLabel' + myClass).attr('visibility', 'hidden');
    mySvg.selectAll('.areaLabel').remove();
    mySvg.select('#yAxisRight' + myClass).attr('visibility', 'hidden');
    mySvg.select('#yAxisRightLabel' + myClass).attr('visibility', 'hidden');

    // ── Format date title ──
    const date = barData.date ? new Date(barData.date) : null;
    let titleDate = '';
    if (date) {
      const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
      titleDate = monthNames[date.getMonth()] + ' ' + date.getFullYear();
    }

    // ── Legend: Back button + date title ──
    const legendGroup = mySvg.select('#legendGroup' + myClass);
    legendGroup.selectAll('*').remove();
    const legendMid = (margins.top / 2) - 20;

    // Back button
    const backG = legendGroup.append('g')
      .style('cursor', 'pointer')
      .on('click', () => { myChart.exitWaterfallMode(); });

    backG.append('rect')
      .attr('x', margins.left - 10).attr('y', legendMid - 12)
      .attr('width', 60).attr('height', 24)
      .attr('rx', 12)
      .attr('fill', '#F0F3F6');
    backG.append('text')
      .attr('x', margins.left + 20).attr('y', legendMid + 3)
      .attr('text-anchor', 'middle')
      .attr('font-family', 'Poppins')
      .attr('font-size', '11px')
      .attr('font-weight', '600')
      .attr('fill', '#1363DF')
      .text('← Back');

    // Title
    legendGroup.append('text')
      .attr('x', width / 2).attr('y', legendMid + 4)
      .attr('text-anchor', 'middle')
      .attr('font-family', 'Poppins')
      .attr('font-size', '13px')
      .attr('font-weight', '600')
      .attr('fill', '#101D42')
      .text('Breakdown' + (titleDate ? ' — ' + titleDate : ''));

    // ── Scales ──
    const xLabels = segments.map(s => s.label);
    const xScale = d3.scaleBand()
      .domain(xLabels)
      .range([0, plotWidth])
      .padding(0.3);

    const yMax = total * 1.15;
    const yScale = d3.scaleLinear()
      .domain([0, yMax])
      .range([chartHeight, 0]);

    // ── Y axis ──
    mySvg.select('#yAxis' + myClass)
      .call(d3.axisLeft(yScale).ticks(5).tickSizeOuter(0)
        .tickFormat((d: any) => {
          if (d === 0) return '';
          if (myChart.props.d3AxisFormatting) return axisTickFormat(myChart.props.yAxisFormatting || '-2~s')(d) + (myChart.props.yAxisSuffix || '');
          return d3.format(',.0f')(d) + (myChart.props.yAxisSuffix || '');
        }))
      .attr('transform', 'translate(' + margins.left + ',' + margins.top + ')');

    // ── X axis with full legend labels ──
    const xTickFormat = (label: string): string => {
      if (label === 'TOTAL') return 'Total';
      const seg = segments.find(s => s.label === label);
      if (seg && seg.key !== '_total') {
        return myChart.props.legendLabels[seg.key] || seg.key;
      }
      return label;
    };

    mySvg.select('#xAxis' + myClass)
      .call(d3.axisBottom(xScale).tickSizeOuter(0).tickFormat(xTickFormat as any))
      .attr('transform', 'translate(' + margins.left + ',' + (margins.top + chartHeight) + ')');

    // Ellipsis + custom tooltip for X-axis labels that overflow
    const bandW = xScale.bandwidth();
    mySvg.selectAll('#xAxis' + myClass + ' text').each(function (this: any) {
      const textEl = d3.select(this);
      const fullText = textEl.text();
      let isTruncated = false;

      // Truncate with ellipsis if text wider than bandwidth
      const node = textEl.node() as SVGTextElement;
      if (node.getComputedTextLength() > bandW) {
        let truncated = fullText;
        while (truncated.length > 1 && node.getComputedTextLength() > bandW - 10) {
          truncated = truncated.slice(0, -1);
          textEl.text(truncated + '..');
        }
        isTruncated = true;
      }

      // Show full label via the component tooltip on hover
      if (isTruncated) {
        textEl
          .style('cursor', 'pointer')
          .on('mouseover', (event: any) => {
            if (!myChart.tooltipEl) return;
            myChart.tooltipEl.innerHTML = '<div style="padding:6px 12px;font-family:Poppins,sans-serif;font-size:11px;font-weight:500;color:#fff;white-space:nowrap">' + fullText + '</div>';
            myChart.tooltipEl.classList.add('show');

            const containerRect = myChart.fs.nativeElement.getBoundingClientRect();
            const ttW = myChart.tooltipEl.offsetWidth || 150;
            const relX = event.pageX - containerRect.left;
            const relY = event.pageY - containerRect.top;
            let left = relX - ttW / 2;
            left = Math.max(0, Math.min(left, containerRect.width - ttW));
            myChart.tooltipEl.style.left = left + 'px';
            myChart.tooltipEl.style.top = (relY - 36) + 'px';
          })
          .on('mouseout', () => {
            myChart.tooltipEl?.classList.remove('show');
          });
      }
    });

    // Hide the xAxisLabel in waterfall mode (no need for "Period" text)
    mySvg.select('#xAxisLabel' + myClass).attr('visibility', 'hidden');

    // Axis styling
    mySvg.selectAll('.axis' + myClass + ' path')
      .attr('visibility', 'visible')
      .style('stroke', '#E8EAEE');
    mySvg.selectAll('.axis' + myClass + ' text')
      .style('font-weight', myChart.props.axisFontWeight)
      .style('font-family', 'Poppins')
      .style('font-size', myChart.props.axisFontSize);
    mySvg.selectAll('#yAxis' + myClass + ' line')
      .attr('x1', 0).attr('x2', plotWidth)
      .style('stroke', '#F0F3F6').style('stroke-width', 1);
    mySvg.selectAll('#xAxis' + myClass + ' line')
      .attr('y1', 0).attr('y2', -chartHeight)
      .style('stroke', '#F0F3F6').style('stroke-width', 1);

    // ── Y axis label ──
    mySvg.select('#yAxisLabel' + myClass)
      .attr('visibility', 'visible')
      .attr('x', margins.left)
      .attr('y', margins.top - 14)
      .attr('text-anchor', 'start')
      .attr('font-family', 'Poppins')
      .attr('fill', myChart.props.yAxisLabelColour || '#737D88')
      .attr('font-size', '12px')
      .attr('font-weight', '400')
      .attr('transform', '')
      .text(myChart.props.yAxisLabel?.toUpperCase() || '');

    mySvg.select('#noDataMessage' + myClass).text('');

    // ── Waterfall clip-path (constrains bars/labels within chart area during zoom) ──
    const wfClipId = 'wfClip' + myClass;
    let wfClip = mySvg.select('defs').select('#' + wfClipId);
    if (wfClip.empty()) {
      wfClip = mySvg.select('defs').append('clipPath').attr('id', wfClipId);
      wfClip.append('rect');
    }
    wfClip.select('rect')
      .attr('x', 0).attr('y', -20)
      .attr('width', plotWidth).attr('height', chartHeight + 20);

    // ── Render waterfall bars ──
    const chartGroup = mySvg.select('#chartGroup' + myClass);
    chartGroup
      .attr('transform', 'translate(' + margins.left + ',' + margins.top + ')')
      .attr('clip-path', 'url(#' + wfClipId + ')');

    const barW = xScale.bandwidth();
    const brProps = myChart.props.barRadius || { value: 4, mode: 'eachTop' };
    const brValue = brProps.value != null ? brProps.value : 4;
    const brMode = brProps.mode || 'eachTop';

    // helper: build SVG path for a rect with per-corner radii
    const wfRrPath = (bx: number, by: number, bw2: number, bh2: number, rtl: number, rtr: number, rbr2: number, rbl: number) => {
      if (bw2 <= 0) bw2 = 0;
      if (bh2 <= 0) bh2 = 0;
      rtl = bh2 > 0 ? Math.min(rtl, bw2 / 2, bh2 / 2) : 0;
      rtr = bh2 > 0 ? Math.min(rtr, bw2 / 2, bh2 / 2) : 0;
      rbr2 = bh2 > 0 ? Math.min(rbr2, bw2 / 2, bh2 / 2) : 0;
      rbl = bh2 > 0 ? Math.min(rbl, bw2 / 2, bh2 / 2) : 0;
      return `M${bx + rtl},${by}`
        + `H${bx + bw2 - rtr}`
        + `A${rtr},${rtr},0,0,1,${bx + bw2},${by + rtr}`
        + `V${by + bh2 - rbr2}`
        + `A${rbr2},${rbr2},0,0,1,${bx + bw2 - rbr2},${by + bh2}`
        + `H${bx + rbl}`
        + `A${rbl},${rbl},0,0,1,${bx},${by + bh2 - rbl}`
        + `V${by + rtl}`
        + `A${rtl},${rtl},0,0,1,${bx + rtl},${by}`
        + 'Z';
    };

    segments.forEach((seg: WfSegment, i: number) => {
      const barX = xScale(seg.label) || 0;
      const barY = yScale(seg.y1);
      const barH = Math.max(0, yScale(seg.y0) - yScale(seg.y1));
      const r = Math.min(brValue, barW / 2, barH / 2);
      // eachTop/top: top corners only; all: all corners; none: no radius
      const rtl = (brMode === 'none') ? 0 : r;
      const rtr = (brMode === 'none') ? 0 : r;
      const rbr = (brMode === 'all') ? r : 0;
      const rbl = (brMode === 'all') ? r : 0;

      // Connector line from previous segment
      if (i > 0) {
        const prevSeg = segments[i - 1];
        const prevBarX = xScale(prevSeg.label) || 0;
        const connectorY = yScale(seg.y1);

        chartGroup.append('line')
          .attr('class', 'wf-connector')
          .attr('x1', prevBarX + barW)
          .attr('x2', barX)
          .attr('y1', connectorY)
          .attr('y2', connectorY)
          .attr('stroke', '#B0B8C4')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '4,3')
          .attr('opacity', 0)
          .transition().duration(400).delay(i * 80)
          .attr('opacity', 1);
      }

      // Bar path with top-only radius
      const barRect = chartGroup.append('path')
        .attr('class', 'wf-bar')
        .attr('fill', seg.colour)
        .style('cursor', 'pointer')
        .on('mouseover', (event: any) => {
          const ttData: any = { date: barData.date };
          if (seg.isTotal) {
            ttData['_total'] = total;
            barKeys.forEach((k: string) => { ttData[k] = Number(barData[k]) || 0; });
          } else {
            ttData[seg.key] = seg.value;
          }
          myChart.showTooltip('waterfall', ttData, event.pageX, event.pageY, width);
        })
        .on('mouseout', () => myChart.hideTooltip('waterfall'));

      // Animate bar growing
      if (myChart.props.animate) {
        barRect
          .attr('d', wfRrPath(barX, yScale(seg.y0), barW, 0, rtl, rtr, rbr, rbl))
          .transition().duration(500).delay(i * 80).ease(d3.easeCubicOut)
          .attr('d', wfRrPath(barX, barY, barW, barH, rtl, rtr, rbr, rbl));
      } else {
        barRect.attr('d', wfRrPath(barX, barY, barW, barH, rtl, rtr, rbr, rbl));
      }

      // Value label on top of bar
      const labelY = barY - 6;
      const suffix = myChart.props.yAxisSuffix || '';
      const fmtVal = Number.isInteger(seg.value) ? d3.format(',')(seg.value) : d3.format(',.1f')(seg.value);

      chartGroup.append('text')
        .attr('class', 'wf-label')
        .attr('x', barX + barW / 2)
        .attr('y', labelY)
        .attr('text-anchor', 'middle')
        .attr('font-family', 'Poppins')
        .attr('font-size', '11px')
        .attr('font-weight', seg.isTotal ? '700' : '600')
        .attr('fill', seg.isTotal ? '#101D42' : '#374151')
        .attr('opacity', 0)
        .text(fmtVal + suffix)
        .transition().duration(400).delay(i * 80 + 200)
        .attr('opacity', 1);

      // Percentage label inside bar (if tall enough)
      if (barH > 24 && !seg.isTotal && total > 0) {
        const pct = ((seg.value / total) * 100).toFixed(1) + '%';
        chartGroup.append('text')
          .attr('class', 'wf-pct')
          .attr('x', barX + barW / 2)
          .attr('y', barY + barH / 2 + 4)
          .attr('text-anchor', 'middle')
          .attr('font-family', 'Poppins')
          .attr('font-size', '10px')
          .attr('font-weight', '500')
          .attr('fill', '#fff')
          .attr('opacity', 0)
          .text(pct)
          .transition().duration(400).delay(i * 80 + 300)
          .attr('opacity', 0.9);
      }
    });

    // ══════════════════════════════════════════════════
    // Y-axis zoom slider for waterfall drill-down
    // ══════════════════════════════════════════════════
    const slider = mySvg.select('#sliderGroup' + myClass);
    slider.selectAll('*').remove();

    if (myChart.props.slider) {
      const wfSliderClass = 'wf-slider';
      const wfXPos = margins.left;
      const wfMin = 0;
      const wfMax = yMax;
      const accentColour = '#e8eaee';

      const sliderGroup = slider.append('g').attr('id', 'sliderGroup-wf');

      const sliderScale = d3.scaleLinear()
        .domain([wfMin, wfMax])
        .range([chartHeight, 0])
        .clamp(true);

      // Track background
      sliderGroup.append('line')
        .attr('class', wfSliderClass + ' wf-track')
        .attr('y1', sliderScale(wfMin)).attr('y2', sliderScale(wfMax))
        .attr('transform', 'translate(' + wfXPos + ',' + margins.top + ')')
        .attr('stroke', '#E8EAEE')
        .attr('stroke-width', 3)
        .attr('stroke-linecap', 'round');

      // Track inset (selected range)
      sliderGroup.append('line')
        .attr('class', wfSliderClass + ' wf-track-inset')
        .attr('y1', sliderScale(yScale.domain()[0])).attr('y2', sliderScale(yScale.domain()[1]))
        .attr('transform', 'translate(' + wfXPos + ',' + margins.top + ')')
        .attr('stroke', accentColour)
        .attr('stroke-width', 3)
        .attr('stroke-linecap', 'round');

      // Redraw waterfall bars when slider changes
      function wfRedraw(): void {
        // Update Y axis
        mySvg.select('#yAxis' + myClass)
          .call(d3.axisLeft(yScale).ticks(5).tickSizeOuter(0)
            .tickFormat((d: any) => {
              if (d === 0) return '';
              if (myChart.props.d3AxisFormatting) return axisTickFormat(myChart.props.yAxisFormatting || '-2~s')(d) + (myChart.props.yAxisSuffix || '');
              return d3.format(',.0f')(d) + (myChart.props.yAxisSuffix || '');
            }))
          .attr('transform', 'translate(' + margins.left + ',' + margins.top + ')');

        mySvg.selectAll('#yAxis' + myClass + ' line')
          .attr('x1', 0).attr('x2', plotWidth)
          .style('stroke', '#F0F3F6').style('stroke-width', 1);
        mySvg.selectAll('.axis' + myClass + ' text')
          .style('font-weight', myChart.props.axisFontWeight)
          .style('font-family', 'Poppins')
          .style('font-size', myChart.props.axisFontSize);

        // Update bars
        const updBrProps = myChart.props.barRadius || { value: 4, mode: 'eachTop' };
        const updBrValue = updBrProps.value != null ? updBrProps.value : 4;
        const updBrMode = updBrProps.mode || 'eachTop';
        const updRrPath = (bx: number, by: number, bw2: number, bh2: number, rtl: number, rtr: number, rbr2: number, rbl: number) => {
          if (bw2 <= 0) bw2 = 0;
          if (bh2 <= 0) bh2 = 0;
          rtl = bh2 > 0 ? Math.min(rtl, bw2 / 2, bh2 / 2) : 0;
          rtr = bh2 > 0 ? Math.min(rtr, bw2 / 2, bh2 / 2) : 0;
          rbr2 = bh2 > 0 ? Math.min(rbr2, bw2 / 2, bh2 / 2) : 0;
          rbl = bh2 > 0 ? Math.min(rbl, bw2 / 2, bh2 / 2) : 0;
          return `M${bx + rtl},${by}`
            + `H${bx + bw2 - rtr}`
            + `A${rtr},${rtr},0,0,1,${bx + bw2},${by + rtr}`
            + `V${by + bh2 - rbr2}`
            + `A${rbr2},${rbr2},0,0,1,${bx + bw2 - rbr2},${by + bh2}`
            + `H${bx + rbl}`
            + `A${rbl},${rbl},0,0,1,${bx},${by + bh2 - rbl}`
            + `V${by + rtl}`
            + `A${rtl},${rtl},0,0,1,${bx + rtl},${by}`
            + 'Z';
        };
        const wfBars = chartGroup.selectAll('.wf-bar').nodes();
        segments.forEach((seg: WfSegment, si: number) => {
          const barX = xScale(seg.label) || 0;
          const barY = yScale(seg.y1);
          const barH = Math.max(0, yScale(seg.y0) - yScale(seg.y1));
          const r = Math.min(updBrValue, barW / 2, barH / 2);
          const rtl = (updBrMode === 'none') ? 0 : r;
          const rtr = (updBrMode === 'none') ? 0 : r;
          const rbr = (updBrMode === 'all') ? r : 0;
          const rbl = (updBrMode === 'all') ? r : 0;
          if (wfBars[si]) {
            d3.select(wfBars[si]).attr('d', updRrPath(barX, barY, barW, barH, rtl, rtr, rbr, rbl));
          }
        });

        // Update connectors
        chartGroup.selectAll('.wf-connector').remove();
        segments.forEach((seg: WfSegment, i: number) => {
          if (i > 0) {
            const prevSeg = segments[i - 1];
            const prevBarX = xScale(prevSeg.label) || 0;
            const barX = xScale(seg.label) || 0;
            chartGroup.append('line')
              .attr('class', 'wf-connector')
              .attr('x1', prevBarX + barW).attr('x2', barX)
              .attr('y1', yScale(seg.y1)).attr('y2', yScale(seg.y1))
              .attr('stroke', '#B0B8C4').attr('stroke-width', 1)
              .attr('stroke-dasharray', '4,3');
          }
        });

        // Update value labels
        chartGroup.selectAll('.wf-label').remove();
        chartGroup.selectAll('.wf-pct').remove();
        const suffix = myChart.props.yAxisSuffix || '';
        segments.forEach((seg: WfSegment) => {
          const barX = xScale(seg.label) || 0;
          const barY = yScale(seg.y1);
          const barH = Math.max(0, yScale(seg.y0) - yScale(seg.y1));
          const fmtVal = Number.isInteger(seg.value) ? d3.format(',')(seg.value) : d3.format(',.1f')(seg.value);

          chartGroup.append('text')
            .attr('class', 'wf-label')
            .attr('x', barX + barW / 2)
            .attr('y', barY - 6)
            .attr('text-anchor', 'middle')
            .attr('font-family', 'Poppins')
            .attr('font-size', '11px')
            .attr('font-weight', seg.isTotal ? '700' : '600')
            .attr('fill', seg.isTotal ? '#101D42' : '#374151')
            .text(fmtVal + suffix);

          if (barH > 24 && !seg.isTotal && total > 0) {
            const pct = ((seg.value / total) * 100).toFixed(1) + '%';
            chartGroup.append('text')
              .attr('class', 'wf-pct')
              .attr('x', barX + barW / 2)
              .attr('y', barY + barH / 2 + 4)
              .attr('text-anchor', 'middle')
              .attr('font-family', 'Poppins')
              .attr('font-size', '10px')
              .attr('font-weight', '500')
              .attr('fill', '#fff')
              .attr('opacity', 0.9)
              .text(pct);
          }
        });
      }

      // Min handle drag
      const dragMin = d3.drag().on('drag', (event: any) => {
        const pos = event.y - margins.top;
        const gap = Math.max(1, wfMax * 0.05);
        const clampedPos = Math.min(sliderScale(wfMin), Math.max(sliderScale(wfMax), pos));
        const newMin = sliderScale.invert(clampedPos);
        const curMax = yScale.domain()[1];
        if (newMin >= wfMin && newMin <= curMax - gap) {
          yScale.domain([newMin, curMax]);
          sliderGroup.select('.wf-min-handle').attr('cy', clampedPos);
          sliderGroup.select('.wf-min-hover').attr('cy', clampedPos);
          sliderGroup.select('.wf-track-inset')
            .attr('y1', sliderScale(newMin)).attr('y2', sliderScale(curMax));
          wfRedraw();
        }
      });

      // Max handle drag
      const dragMax = d3.drag().on('drag', (event: any) => {
        const pos = event.y - margins.top;
        const gap = Math.max(1, wfMax * 0.05);
        const clampedPos = Math.max(sliderScale(wfMax), Math.min(sliderScale(wfMin), pos));
        const newMax = sliderScale.invert(clampedPos);
        const curMin = yScale.domain()[0];
        if (newMax <= wfMax && newMax >= curMin + gap) {
          yScale.domain([curMin, newMax]);
          sliderGroup.select('.wf-max-handle').attr('cy', clampedPos);
          sliderGroup.select('.wf-max-hover').attr('cy', clampedPos);
          sliderGroup.select('.wf-track-inset')
            .attr('y1', sliderScale(curMin)).attr('y2', sliderScale(newMax));
          wfRedraw();
        }
      });

      // Min handle
      const minHover = sliderGroup.append('circle')
        .attr('class', 'wf-min-hover')
        .attr('cy', sliderScale(yScale.domain()[0]))
        .attr('transform', 'translate(' + wfXPos + ',' + margins.top + ')')
        .attr('r', myChart.handleRadius + 8)
        .attr('fill', accentColour).attr('opacity', 0).attr('pointer-events', 'none');
      const minHandle = sliderGroup.append('circle')
        .attr('class', 'wf-min-handle')
        .attr('cy', sliderScale(yScale.domain()[0]))
        .attr('transform', 'translate(' + wfXPos + ',' + margins.top + ')')
        .attr('r', myChart.handleRadius)
        .attr('fill', accentColour).attr('stroke', 'white').attr('stroke-width', 2)
        .style('cursor', 'ns-resize')
        .call(dragMin);
      minHandle.on('mouseover', () => minHover.transition().duration(200).attr('opacity', 0.4))
        .on('mouseout', () => minHover.transition().duration(400).attr('opacity', 0));

      // Max handle
      const maxHover = sliderGroup.append('circle')
        .attr('class', 'wf-max-hover')
        .attr('cy', sliderScale(yScale.domain()[1]))
        .attr('transform', 'translate(' + wfXPos + ',' + margins.top + ')')
        .attr('r', myChart.handleRadius + 8)
        .attr('fill', accentColour).attr('opacity', 0).attr('pointer-events', 'none');
      const maxHandle = sliderGroup.append('circle')
        .attr('class', 'wf-max-handle')
        .attr('cy', sliderScale(yScale.domain()[1]))
        .attr('transform', 'translate(' + wfXPos + ',' + margins.top + ')')
        .attr('r', myChart.handleRadius)
        .attr('fill', accentColour).attr('stroke', 'white').attr('stroke-width', 2)
        .style('cursor', 'ns-resize')
        .call(dragMax);
      maxHandle.on('mouseover', () => maxHover.transition().duration(200).attr('opacity', 0.4))
        .on('mouseout', () => maxHover.transition().duration(400).attr('opacity', 0));
    }
  }

  // ══════════════════════════════════════════════════════
  // STEP 3: getChartData() — Config-Driven Props Setup
  // ══════════════════════════════════════════════════════
  getChartData(): void {
      this.chartData = this.inputData;
      if (!this.chartData || this.chartData.length === 0) {
      this.isLoading = false;
      this.noData = true; this.noDataNotifier.report('waterfall-chart');
      return;
    }
    
      const cfg = this.item?.config || {};
      if(cfg?.AggregationEnabled == true){
        const { groups, summary } = this.getAvgBusinessHours(this.inputData, cfg);
        this.chartData = groups
      }else{
      this.chartData = this.inputData;
      }

    this.props = {
      colours: cfg.colours || {
          benefit_not_covered: '#E43D3D',
          buy_ana_bill: '#FF6749',
          pa_required: '#FFAF59',
          pred_recommended: '#65C668',
          pred_required: '#5AA641',
        },
      legendLabels: cfg.legendLabels || {
          benefit_not_covered: 'Benefits Not Covered',
          buy_ana_bill: 'Buy & Bill',
          pa_required: 'Prior Authorization Required',
          pred_recommended: 'Predicted Recommendation',
          pred_required: 'Predicted Requirement',
        },
      barKeys: cfg.barKeys ? (Array.isArray(cfg.barKeys) ? cfg.barKeys : cfg.barKeys.split(',').filter((k: string) => k)) : ['benefit_not_covered','buy_ana_bill','pa_required','pred_recommended','pred_required'],
      lineKeys: cfg.lineKeys ? (Array.isArray(cfg.lineKeys) ? cfg.lineKeys : cfg.lineKeys.split(',').filter((k: string) => k)) : [],
      areaKeys: cfg.areaKeys ? (Array.isArray(cfg.areaKeys) ? cfg.areaKeys : cfg.areaKeys.split(',').filter((k: string) => k)) : [],
      barMode: cfg.barMode || 'stacked',
      stackedArea: cfg.stackedArea === true || cfg.stackedArea === 'true' || false,
      lineOnPrimaryAxis: cfg.lineOnPrimaryAxis === true || cfg.lineOnPrimaryAxis === 'true' || false,
      period: this.activePeriod,
      xAxisLabel: cfg.xAxisLabel || 'Period',
      yAxisLabel: cfg.yAxisLabel || 'reasons',
      yAxisRightLabel: cfg.yAxisRightLabel || '',
      yAxisLabelColour: cfg.yAxisLabelColour || '#737D88',
      yAxisRightLabelColour: cfg.yAxisRightLabelColour || '#737D88',
      xAxisVar: cfg.xAxisVar || 'period',
      showBrush: cfg.showBrush === 'true' || cfg.showBrush === true ||true,
      d3AxisFormatting: cfg.d3AxisFormatting === 'true' || cfg.d3AxisFormatting === true,
      yAxisFormatting: cfg.yAxisFormatting || '-2~s',
      yAxisSuffix: cfg.yAxisSuffix || '',
      yAxisRightFormatting: cfg.yAxisRightFormatting || '-2~s',
      yAxisRightSuffix: cfg.yAxisRightSuffix || '',
      axisFontSize: Number(cfg.axisFontSize) || 12,
      axisFontWeight: Number(cfg.axisFontWeight) || 700,
      chartHeight: Number(cfg.chartHeight) || 500,
      partialPeriod: cfg.partialPeriod === 'true' || cfg.partialPeriod === true,
      slider: cfg.slider === 'true' || cfg.slider === true||true,
      animate: cfg.animate === 'true' || cfg.animate === true,
      barRadius: cfg.barRadius || { value: 4, mode: 'eachTop' },
      hoverLine: cfg.hoverLine || { show: true, colour: '#B0B0B0', width: 1, dash: '4,3' },
      crosshair: { bar: true, line: true, area: true },
      // 'all'   → crosshair for every visible series, hover anywhere in the plot
      // 'hover' → crosshair only for the bar / dot / area under the cursor
      crosshairMode: 'hover', //cfg.crosshairMode === 'hover' ? 'hover' : 'all',
      lineDot: cfg.lineDot || { show: true, radius: 4, fill: 'white', strokeWidth: 2 },
      dataLabels: cfg.dataLabels || { bar: false, line: false, area: false, total: false, value: false, fontSize: 11, fontWeight: 600, totalFontWeight: 700, color: '#333', totalColor: '#333', format: '-2~s' },
      noDataMessage: cfg.noDataMessage || 'There is no data for this time period',
    };
  console.log('Watter Fall Chart props:', this.props);
  console.log('Watter Fall Chart data:', this.chartData);


    this.noData = false;
    this.isLoading = false;
    this.plotChart();
  }
   /**
     * Aggregate raw rows by `period` and compute the average business-hour TAT
     * (total_bi_tat_hours / total_bi_tat_req) per period plus an overall summary.
     *
     * Mirrors the spreadsheet formula =SUM(total_bi_tat_hours)/SUM(total_bi_tat_req).
     * Pass `scale: 60` via the second argument to convert hours → minutes.
     */
    getAvgBusinessHours(rows: any[], cfg: any) {
      const config: AggregationConfig = {
        groupBy:cfg['aggregation_groupBy'] ||'period',
        fields:cfg['aggregation_fields']||{
          total_bi_tat_hours :   { source: 'total_bi_tat_hours',    op: 'sum' },
          total_bi_tat_req   :   { source: 'total_bi_tat_req',      op: 'sum' },
          bi_tat_less_than_60:   { source: 'bi_tat_less_than_60',   op: 'sum' },
          bi_tat_less_than_8h:   { source: 'bi_tat_less_than_8h',   op: 'sum' },
          bi_tat_more_than_8h:   { source: 'bi_tat_more_than_8h',   op: 'sum' },
        },
        computed: cfg['aggregation_computed'] || {
          avg_bi_hours: {
            op: 'ratio',
            numerator: 'total_bi_tat_hours',
            denominator: 'total_bi_tat_req',
            scale:  1,
            round:  2,
          },
        },
      };
  
      const result = this.dataAggregation.aggregate(rows, config);
      return { groups: result.groups, summary: result.summary };
    }
}
